/**
 * The edit model's reply, applied to text.
 *
 * On the flag-on path the model does not retype a section. It returns a short
 * list of operations, each naming the paragraph it acts on by its
 * `_dioptra_p_*` bookmark (`paragraph_id`) and, where it needs one, quoting
 * the exact span of that paragraph's accepted text as its `anchor`. This
 * module turns those operations into the desired text per paragraph that
 * `ProposalBuilder` consumes, and names the first operation that cannot be
 * applied so the core can hand the reason back to the model and ask again.
 *
 * The operation vocabulary mirrors the core's `review/edit_operations.py`,
 * which live review has used in production since 2026-08-17. The Python stays
 * for the flag-off path; this is the one implementation the web routes and the
 * add-in run.
 *
 * WHY no character is ever added or removed here
 *   The text an operation carries is written into the paragraph exactly as the
 *   model gave it. Where it would read wrong at its edges — two sentences or two
 *   words glued, two spaces, a line break, an accent cut off its letter — the
 *   operation is handed back with what would read there, and the model resends.
 *   A rule that fixed the text instead would be right for one language and
 *   wrong for another: a space added between two sentences is a corruption in
 *   Japanese, which writes none.
 *
 * WHY a replacement's retyped characters never reach the document
 *   A replacement (`replace_span`, `replace_paragraph`) retypes the words it
 *   keeps, and a model retypes Word's typographic apostrophe, quotes and dashes
 *   and its non-breaking spaces as a keyboard spells them. The desired text this
 *   module reports carries them as the model typed them; the engine that writes
 *   it (`TextEditPlanner`) compares the document and the desired text under the
 *   one fold every reading of the text shares (`WordTokenizer.comparisonKey`,
 *   the fold that finds an anchor here), so a word retyped in another form is
 *   the document's word and nothing is written for it (R13, 2026-10-16:
 *   "days’ notice" -> "days' written notice" inserts "written" alone).
 *
 * The routes validate one list holding paragraph operations, the revert of a
 * pending change (`revert_change`, applied by `revert-operations.ts` on the
 * document's markup before this applier runs) and the table operations of
 * `table-operations.ts` (`editOperationSchema`). The three appliers walk that
 * same list and each skips the others' operations in place, so an operation's
 * index is its position in the list the model sent whichever applier asks again
 * it.
 */
import { z } from "zod";

import { LEFT_OUT, type OperationReaskPolicy, type ReasonWrittenAsGiven, WRITTEN_AS_GIVEN, WRITTEN_AS_GIVEN_AS_ITS_MESSAGE_SAYS } from "./operation-reask-policy";
import { freshParagraphBookmarkName } from "./ooxml/paragraph-bookmark-naming";
import { ParagraphLinks } from "./ooxml/paragraph-links";
import type { DocumentParagraph, ParagraphLink, TableOfContentsEntry } from "./wire/document-paragraph";
import { type LinkAction, linkActionSchema, type LinkInstruction } from "./wire/link-instruction";
import { paragraphListContinuationSchema, type ParagraphListContinuation } from "./wire/redline-payload";
import type { RevertChangeReaskReason } from "./redline-engine";
import { isTableEditOperation, tableEditOperationSchema } from "./table-operations";
import { TextEditPlanner } from "./text-edit-planner";
import { WordTokenizer } from "./text/word-tokenizer";

const paragraphScopedOperationFields = { paragraph_id: z.string().min(1) };

/**
 * What a replacement says of a link whose words it changes (`LinkInstruction`): `link`,
 * and with "change" the new address in `link_url`.
 *
 * NULL, or absent
 *   The replacement changes no link's words; one that does is asked again
 *   (`linkInstructionMissing`). The core sends null for a field it left empty.
 */
const linkInstructedOperationFields = { link: linkActionSchema.nullish(), link_url: z.string().nullish() };

export const paragraphEditOperationSchema = z.discriminatedUnion("op", [
    z.object({ op: z.literal("append_sentence"), ...paragraphScopedOperationFields, text: z.string() }),
    z.object({ op: z.literal("insert_after_anchor"), ...paragraphScopedOperationFields, anchor: z.string(), text: z.string() }),
    z.object({ op: z.literal("insert_before_anchor"), ...paragraphScopedOperationFields, anchor: z.string(), text: z.string() }),
    /**
     * `occurrence`, counted from 1, names which of several stretches reading as `anchor`
     * is replaced (`locateNthOccurrence`); absent, the anchor must occur once.
     */
    z.object({
        op: z.literal("replace_span"),
        ...paragraphScopedOperationFields,
        anchor: z.string().optional(),
        from: z.string().optional(),
        to: z.string().optional(),
        text: z.string(),
        occurrence: z.number().int().positive().optional(),
        ...linkInstructedOperationFields,
    }),
    z.object({ op: z.literal("delete_span"), ...paragraphScopedOperationFields, anchor: z.string().optional(), from: z.string().optional(), to: z.string().optional() }),
    z.object({ op: z.literal("replace_paragraph"), ...paragraphScopedOperationFields, text: z.string(), ...linkInstructedOperationFields }),
    z.object({ op: z.literal("insert_paragraph_after"), ...paragraphScopedOperationFields, text: z.string(), list: paragraphListContinuationSchema }),
    z.object({ op: z.literal("delete_paragraph"), ...paragraphScopedOperationFields }),
    /**
     * `change_id` is the id the reader listed the pending change under in the paragraph's
     * `<changes>` ("c1", "c2", …); `text` is how a deletion's restored words should read, null
     * for exactly as they were struck, and always null for an insertion.
     */
    z.object({ op: z.literal("revert_change"), ...paragraphScopedOperationFields, change_id: z.string().min(1), text: z.string().nullable() }),
]);

export type ParagraphEditOperation = z.infer<typeof paragraphEditOperationSchema>;

/** The revert of one pending tracked change, which `RevertOperations.apply` makes on the document's markup. */
export type RevertChangeOperation = Extract<ParagraphEditOperation, { op: "revert_change" }>;

export const isRevertChangeOperation = (operation: EditOperation): operation is RevertChangeOperation => operation.op === "revert_change";

/** Every operation the routes accept: the paragraph operations above and the table operations, in one list. */
export const editOperationSchema = z.union([paragraphEditOperationSchema, tableEditOperationSchema]);

export type EditOperation = z.infer<typeof editOperationSchema>;

/** One edited paragraph: the bookmark that names it and the text it should read after the operations. */
export interface DesiredParagraphText {
    bookmark: string;
    textProposed: string;
    /**
     * What becomes of each link whose words the operations change, as the replacement
     * that changed them said (`LinkInstruction`).
     *
     * EMPTY
     *   No operation changed a link's words.
     */
    linkInstructions: LinkInstruction[];
}

/** A paragraph operation that carries a link instruction (`linkInstructedOperationFields`). */
type LinkInstructedOperation = Extract<ParagraphEditOperation, { op: "replace_span" | "replace_paragraph" }>;

/**
 * One new paragraph the model asked for, placed after the paragraph `afterBookmark`
 * names, with the id it is written under (`bookmark`).
 *
 * WHY the new paragraph has its id before it is written
 *   Whatever is proposed with it names it: a comment the card carries on the new
 *   paragraph sits on this id, and the applier writes the paragraph already
 *   bookmarked with it, so the comment and Go to issue find it by its id.
 */
export interface DesiredParagraphInsertion {
    afterBookmark: string;
    bookmark: string;
    textProposed: string;
    /** Whether the new paragraph continues its neighbour's list or stands plain (`ParagraphListContinuation`). */
    list: ParagraphListContinuation;
}

/** One operation moved off a table of contents entry onto the heading that entry lists, so a card can say why a heading the model never named changed. */
export interface TableOfContentsRedirect {
    entryBookmark: string;
    headingBookmark: string;
}

export interface EditOperationsApplyResult {
    paragraphs: DesiredParagraphText[];
    insertions: DesiredParagraphInsertion[];
    tableOfContentsRedirects: TableOfContentsRedirect[];
}

/** The operations that quote a span of the paragraph; the same words read in the heading a table of contents entry lists, so these move there. */
const SPAN_OPERATIONS = new Set<ParagraphEditOperation["op"]>(["replace_span", "delete_span", "insert_after_anchor", "insert_before_anchor"]);

export interface EditOperationsApplyInput {
    /** The model's whole list; the table operations in it are `TableOperations.apply`'s and are skipped here. */
    operations: readonly EditOperation[];
    paragraphs: DocumentParagraph[];
    /**
     * The 1-based indexes of the operations the model answered again after a re-ask
     * (`OperationListReask.askedOnce`): each is written as given, its seams not asked
     * about again, or, for a reason with nothing as given to write, left out
     * (`operationIndexesLeftOut`).
     *
     * NULL, or absent
     *   A core from before ask-once re-asks posts none and cannot mark an answer
     *   confirmed: the ask-once checks stay off for it, and a full stop before a comma
     *   is asked about as it was before (the text's own "." only), so no answer is
     *   asked about forever.
     */
    confirmedOperationIndexes?: readonly number[] | null;
    /**
     * The 1-based indexes of the operations `applyOperationList` leaves out: each was
     * confirmed, and its reason has nothing as given to write. Skipped in place, so every
     * other operation keeps its `edit_ops[N]`.
     *
     * NULL, or absent
     *   Nothing is left out: a caller outside `applyOperationList` (a change throughout).
     */
    operationIndexesLeftOut?: ReadonlySet<number>;
    /**
     * Every bookmark name in the document the paragraphs were read from, so a new
     * paragraph's id is one no paragraph of the document carries
     * (`freshParagraphBookmarkName`): a caller that posts one paragraph (an added
     * paragraph, a live-review step) still mints against the whole document, since the
     * applier's `insertBookmark` would move another paragraph's id of the same name.
     *
     * NULL, or absent
     *   A core from before it posts none: the ids are minted against the posted
     *   paragraphs' names, as they were.
     */
    documentBookmarkNames?: readonly string[] | null;
}

/**
 * The ask-once checks of one apply: which operations the core's re-ask loop already
 * asked about once and the model answered again (`EditOperationsApplyInput.confirmedOperationIndexes`).
 * Null throughout this file for a core that posts no confirmations.
 */
interface SeamConfirmations {
    confirmedOperationIndexes: ReadonlySet<number>;
}

export type EditOperationReaskReason =
    | "unknownParagraph"
    | "emptyAnchor"
    | "anchorMissing"
    /** The anchor names words that are struck in the paragraph: they read in the text with changes rejected and nowhere in the current text. */
    | "anchorStruck"
    /** `from` and `to` both resolve, in the other order: swapping them names a real span. */
    | "spanReversed"
    | "anchorAmbiguous"
    /** A `replace_span`'s `occurrence` names more occurrences of its anchor than the paragraph as shown holds. */
    | "occurrenceMissing"
    /** A `replace_span` names an `occurrence`, and another operation of its paragraph quotes words the paragraph as shown does not hold, so the count cannot be kept. */
    | "occurrenceBesideWrittenText"
    | "anchorOverlap"
    | "emptyText"
    /** The text of an operation inside a paragraph starts or ends with a line break, which stays a break inside that paragraph and never starts a new one. */
    | "textLineBreakAtEdge"
    /** Where the text meets the paragraph, a sentence's closing punctuation runs into a letter with no space, where the paragraph had no such pair. */
    | "textGluesSentences"
    /** Where the text meets the paragraph, two letters of scripts that space their words meet, where the paragraph had a word boundary. */
    | "textGluesWords"
    /** Where the text meets the paragraph, or where a deletion closes, two whitespace characters meet (a space, a tab, a no-break space), where the paragraph had one gap. */
    | "textDoublesSpace"
    /** Where the text meets the paragraph, or where a deletion closes, one punctuation mark meets itself ("prior year.."), or, for a core that posts no confirmations, the text's own "." meets the paragraph's "," ("Mgmt.,"), where the paragraph had no such pair. */
    | "textDoublesPunctuation"
    /** Asked once (`askedOnce`): where the text meets the paragraph, or where a deletion closes, a "." meets a "," whichever side wrote the "." ("the Services., and"), where the paragraph had no such pair. */
    | "textFullStopBeforeComma"
    /** Asked once (`askedOnce`): a word the operation writes holds a mark the fold reads as one the paragraph (for a new paragraph, the paragraph it follows) writes in another form only (a straight apostrophe where the paragraph writes "’", a hyphen where it writes "–"). */
    | "textRetypesDocumentMark"
    /** The anchor or span ends between a character and the mark stored after it (an accent as its own character, a Thai vowel sign), so the mark would land on other text. */
    | "textDetachesAccent"
    /** The paragraph is a table of contents entry, which Word rewrites from the headings: a whole-paragraph operation on it, or any operation on an entry that lists no heading the list carries. */
    | "tableOfContentsEntry"
    /** The engine handed back the revert a `revert_change` asked for: the id names no change as it was shown, or one that cannot be reverted (`RevertChangeReaskReason`). */
    | RevertChangeReaskReason
    /** A `revert_change` names a paragraph in a table row that is struck as a whole; a copy written into its cell would go with the row on Accept All. */
    | "rowStruck"
    /** A replacement changes the words of a link and says nothing of the link (`link`). */
    | "linkInstructionMissing"
    /** A replacement says `link` "change" with no `link_url`, or one that does not read as an address. */
    | "linkAddressMissing"
    /** Asked once (`askedOnce`): a replacement keeps a link around new words that share none of its words. */
    | "linkKeptOverOtherWords"
    /** Asked once (`askedOnce`): a replacement keeps the address of a link whose words are that address, and changes the words. */
    | "linkTextIsItsAddress"
    /** Asked once (`askedOnce`): an operation writes a web address as plain words, which opens nothing. */
    | "textTypesAnAddress";

/**
 * Every reason the web hands an operation back for, with what the model's resend of
 * the same operation does (`OperationReaskPolicy`).
 *
 * WHY one table, typed over every reason
 *   A check whose re-ask the model could not answer by resending left a correct answer no
 *   way through: `textGluesWords` handed back "GES" inserted after "CHAN" as two words glued
 *   together, five correct answers over two cards, and neither card was made (2026-10-23).
 *   The record is typed over `EditOperationReaskReason`, so a new reason does not compile
 *   until it says what a resend does, and only a reason written as given may be let through
 *   by its check (`askAgainUnlessConfirmed`).
 */
export const EDIT_OPERATION_REASK_POLICIES = {
    unknownParagraph: LEFT_OUT,
    emptyAnchor: LEFT_OUT,
    anchorMissing: LEFT_OUT,
    anchorStruck: LEFT_OUT,
    spanReversed: LEFT_OUT,
    anchorAmbiguous: LEFT_OUT,
    occurrenceMissing: LEFT_OUT,
    occurrenceBesideWrittenText: LEFT_OUT,
    anchorOverlap: LEFT_OUT,
    emptyText: LEFT_OUT,
    textLineBreakAtEdge: WRITTEN_AS_GIVEN,
    textGluesSentences: WRITTEN_AS_GIVEN,
    textGluesWords: WRITTEN_AS_GIVEN,
    textDoublesSpace: WRITTEN_AS_GIVEN,
    textDoublesPunctuation: WRITTEN_AS_GIVEN,
    textFullStopBeforeComma: WRITTEN_AS_GIVEN_AS_ITS_MESSAGE_SAYS,
    textRetypesDocumentMark: WRITTEN_AS_GIVEN_AS_ITS_MESSAGE_SAYS,
    textDetachesAccent: WRITTEN_AS_GIVEN,
    tableOfContentsEntry: LEFT_OUT,
    unknownChange: LEFT_OUT,
    staleChange: LEFT_OUT,
    ambiguousChange: LEFT_OUT,
    textOnInsertion: LEFT_OUT,
    nothingToRevert: LEFT_OUT,
    paragraphStruck: LEFT_OUT,
    rowStruck: LEFT_OUT,
    linkInstructionMissing: LEFT_OUT,
    linkAddressMissing: LEFT_OUT,
    linkKeptOverOtherWords: WRITTEN_AS_GIVEN_AS_ITS_MESSAGE_SAYS,
    linkTextIsItsAddress: WRITTEN_AS_GIVEN_AS_ITS_MESSAGE_SAYS,
    textTypesAnAddress: WRITTEN_AS_GIVEN_AS_ITS_MESSAGE_SAYS,
} as const satisfies Record<EditOperationReaskReason, OperationReaskPolicy>;

/** The reasons whose check lets an operation the model confirmed through, written as given. */
type EditOperationReaskWrittenAsGiven = ReasonWrittenAsGiven<typeof EDIT_OPERATION_REASK_POLICIES>;

/**
 * The first operation that could not be applied. `operationIndex` is 1-based
 * so the message reads the way the model was told to think about its list
 * (`edit_ops[2]`), and `message` is written for the model: it says what to
 * change before resubmitting.
 */
export class EditOperationReask extends Error {
    /** What the model's resend of this operation does, from the reason's row of `EDIT_OPERATION_REASK_POLICIES`. */
    readonly policy: OperationReaskPolicy;

    constructor(
        readonly operationIndex: number,
        readonly reason: EditOperationReaskReason,
        message: string,
    ) {
        super(message);
        this.name = "EditOperationReask";
        this.policy = EDIT_OPERATION_REASK_POLICIES[reason];
    }
}

/** A mark combined onto the character before it, such as an accent stored as its own character (U+0301). */
const COMBINING_MARK = /\p{M}/u;

/** How many characters of an anchor a re-ask message quotes before eliding. */
const ANCHOR_PREVIEW_LIMIT = 80;

/** A line break at the very start or the very end of a text, `<br/>` already decoded to "\n". */
const LINE_BREAK_AT_TEXT_EDGE = /^[\r\n]|[\r\n]$/;

/** How many characters either side of a join a re-ask message quotes. */
const JOIN_PREVIEW_CHARACTERS_PER_SIDE = 20;

/** A word this short or shorter must match letter for letter: one letter apart is a different word ("may"/"way"). */
const SHORTEST_WORD_THAT_MAY_DIFFER = 4;
/** Past this length two letters may differ, which is what a regional spelling changes in a long word ("authorisation"/"authorization"). */
const LONGER_WORD_LENGTH = 8;

/**
 * The marks the core's prompts wrap reference text in (`⟦ref: Section 7.19⟧`):
 * a cross-reference, page number, date, caption number or equation, which Word
 * draws from its own markup until an edit reaches it and the engine unlinks it.
 * The marks are the prompt's, never the document's, so both an anchor and the
 * words an operation writes are read with them removed.
 */
const REFERENCE_MARK_OPEN = "⟦ref: ";
const REFERENCE_MARK_CLOSE = "⟧";

/**
 * One paragraph's running text while its operations are applied, with the
 * bookmark the first operation to reach it named it by, so each operation sees
 * what the ones before it wrote. A later operation naming the same paragraph by
 * another of its bookmarks continues this same running text and leaves
 * `bookmark` as the first name, which is the name the desired text is reported
 * under.
 */
interface WorkingParagraph {
    bookmark: string;
    paragraphIndex: number;
    text: string;
    /**
     * The paragraph with every tracked change rejected, as it was before its changes:
     * the one text in which struck words still read. Never edited and never anchored
     * on; it exists so a re-ask can tell an anchor on struck words from one on words
     * that were never there.
     */
    textWithChangesRejected: string;
    resolvedAnchors: ResolvedAnchor[];
    /** The paragraph's accepted text as the model was shown it: the text `linksAsShown` index into. */
    textAsShown: string;
    /** The paragraph's links as the reader read them (`DocumentParagraph.links`), in `textAsShown`'s offsets. */
    linksAsShown: ParagraphLink[];
    /** What becomes of each link an operation changed the words of (`recordLinkInstructions`). */
    linkInstructions: LinkInstruction[];
}

/**
 * Where one operation lands: the running text it edits, and the table of contents
 * entry it named when it was moved onto that paragraph (null when it named the
 * paragraph itself).
 */
interface ResolvedOperationTarget {
    working: WorkingParagraph;
    redirectedFromBookmark: string | null;
}

/** Where one operation landed, in the document's own characters, for reporting back to the model. */
interface ResolvedAnchor {
    operationIndex: number;
    documentText: string;
}

/**
 * One span operation held back until every operation of its paragraph has been
 * read, so all of them are resolved against the paragraph the model was shown.
 */
interface PendingSpanOperation {
    operation: Extract<ParagraphEditOperation, { op: "append_sentence" | "insert_after_anchor" | "insert_before_anchor" | "replace_span" | "delete_span" }>;
    operationIndex: number;
    /**
     * The table of contents entry the operation named when it was moved onto this
     * paragraph, the heading that entry lists, so a re-ask of the moved operation is
     * restated with the move first.
     *
     * NULL
     *   The operation named the paragraph itself.
     */
    redirectedFromBookmark: string | null;
}

/**
 * Where a quote matched more than once: the paragraph's own text and each matched
 * span in it. A reading that matched on a folded form maps its matches back first,
 * so the re-ask quotes the document's characters, never the fold's.
 */
interface AmbiguousMatches {
    text: string;
    spans: AnchorSpan[];
}

/** Where an anchor was found in the running text: `start` inclusive, `end` exclusive. */
interface AnchorSpan {
    start: number;
    end: number;
}

/** One stretch of a paragraph that reads as a quote (`EditOperations.occurrencesOf`): its span in the paragraph's characters, and those characters. */
export interface QuoteOccurrence extends AnchorSpan {
    text: string;
}

/** The two characters of the paragraph either side of a cut, as it read before the operation; "" past its start or end. */
interface ParagraphCharactersAtCut {
    characterBeforeCut: string;
    characterAfterCut: string;
}

/**
 * One place where an operation's text meets the paragraph's own text, in the
 * paragraph as the operation writes it, with the characters the paragraph held at
 * the cut that join replaces.
 */
interface TextJoin {
    /** Index, in `ParagraphTextAsWritten.text`, of the first character after the join. */
    joinOffset: number;
    /**
     * True when what follows the join is the paragraph's own text (the rest of the
     * paragraph, or the anchor an insertion goes before), false when it is the
     * operation's text.
     */
    paragraphTextFollowsJoin: boolean;
    /**
     * The neighbouring characters the join stands in for: one pair for an insertion
     * or for either end of a replacement, two for a deletion, whose two cuts close
     * into one join.
     */
    paragraphCharactersReplacedByJoin: ParagraphCharactersAtCut[];
}

/** A paragraph's text with one operation's text written into it exactly as given, and every join that writing makes. */
interface ParagraphTextAsWritten {
    text: string;
    joins: TextJoin[];
}

export class EditOperations {
    /**
     * Applies every operation in order and reports the desired text of each
     * paragraph an operation touched, in document order, plus the new
     * paragraphs asked for and the operations moved off a table of contents
     * entry onto the heading it lists (`tableOfContentsRedirects`). Throws
     * `EditOperationReask` at the first operation that cannot be applied;
     * nothing is partially returned.
     */
    static apply(input: EditOperationsApplyInput): EditOperationsApplyResult {
        const workingParagraphs = new Map<number, WorkingParagraph>();
        const pendingSpanOperations = new Map<number, PendingSpanOperation[]>();
        const insertions: DesiredParagraphInsertion[] = [];
        /**
         * Every bookmark name of the document and of the posted paragraphs, and each id
         * already given to a new paragraph of this list, so the next new paragraph's id is
         * one no paragraph of the document carries (`freshParagraphBookmarkName`).
         *
         * NULL documentBookmarkNames
         *   A core from before it: the posted paragraphs' names alone.
         */
        const paragraphIdsInUse = new Set([...input.paragraphs.flatMap((paragraph) => paragraph.bookmarkNames), ...(input.documentBookmarkNames ?? [])]);
        const tableOfContentsRedirects: TableOfContentsRedirect[] = [];
        /**
         * CASES
         *   - the core posted confirmations, an empty list included -> the ask-once checks run, each
         *                                                              confirmed operation written as given
         *   - it posted none                                         -> null: a core from before them
         */
        const confirmationsPosted = input.confirmedOperationIndexes !== undefined && input.confirmedOperationIndexes !== null;
        const seamConfirmations: SeamConfirmations | null = confirmationsPosted ? { confirmedOperationIndexes: new Set(input.confirmedOperationIndexes) } : null;
        for (const [zeroBasedIndex, operation] of input.operations.entries()) {
            /**
             * EXAMPLE
             *   `zeroBasedIndex` = 1, the second operation in the list
             *   `+ 1`            -> 2, the number the model sees in `edit_ops[2]`
             */
            const operationIndex = zeroBasedIndex + 1;
            /**
             * WHEN the model confirmed this operation and its reason has nothing as given to write
             *   CONSEQUENCE
             *     it is left out (`applyOperationList`); skipping it here keeps every other
             *     operation's `operationIndex` the position in the list the model sent.
             */
            if (input.operationIndexesLeftOut?.has(operationIndex)) continue;
            /**
             * WHEN the operation is a table operation
             *   CONSEQUENCE
             *     `TableOperations.apply` handles it from this same list; skipping it here keeps
             *     `operationIndex` the position in the list the model sent.
             */
            if (isTableEditOperation(operation)) continue;
            /**
             * WHEN the operation is a revert
             *   CONSEQUENCE
             *     `RevertOperations.apply` made it already, on the document's markup, and the
             *     paragraphs this applier was given read as they do once reverted; skipping it
             *     here keeps `operationIndex` the position in the list the model sent.
             */
            if (isRevertChangeOperation(operation)) continue;
            const { working, redirectedFromBookmark } = EditOperations.workingParagraphFor(operation, input.paragraphs, workingParagraphs, operationIndex);
            /**
             * WHEN the operation was moved off a table of contents entry onto this heading
             *   CONSEQUENCE
             *     the move is reported once per entry and heading, however many operations took it.
             */
            if (redirectedFromBookmark !== null) EditOperations.recordRedirect(tableOfContentsRedirects, redirectedFromBookmark, working.bookmark);
            /**
             * CASES, by what the operation does to the paragraph
             *   - `insert_paragraph_after` -> records an insertion, the paragraph itself is untouched; a
             *                                 mark the new paragraph writes in another form than the
             *                                 paragraph it follows is asked about once
             *                                 (`askOnceWhenNewParagraphRetypesDocumentMarks`)
             *   - `delete_paragraph`       -> the paragraph's desired text becomes empty
             *   - `replace_paragraph`      -> the paragraph's desired text becomes the given text; a word it
             *                                 retypes in another form is the paragraph's word to the engine
             *                                 that writes it (`TextEditPlanner`), and a mark in a word it
             *                                 changes is asked about once (`askOnceWhenTextRetypesDocumentMarks`)
             *   - the four span operations -> splice into the running text
             */
            if (operation.op === "insert_paragraph_after") {
                const wordsToWrite = EditOperations.requireWordsToWrite(operation.text, operation.op, operationIndex);
                EditOperations.askOnceWhenNewParagraphRetypesDocumentMarks(working.text, wordsToWrite, operation.op, operationIndex, seamConfirmations);
                EditOperations.askOnceWhenTextWritesAnAddressAsWords(working.text, wordsToWrite, operation, operationIndex, seamConfirmations);
                const newParagraphId = freshParagraphBookmarkName(paragraphIdsInUse);
                paragraphIdsInUse.add(newParagraphId);
                insertions.push({ afterBookmark: operation.paragraph_id, bookmark: newParagraphId, textProposed: wordsToWrite, list: operation.list });
                continue;
            }
            if (operation.op === "delete_paragraph") {
                /**
                 * WHEN this paragraph has span operations waiting
                 *   CONSEQUENCE
                 *     they are applied first, so a rewrite of the whole paragraph still
                 *     lands after the operations the model listed before it.
                 */
                EditOperations.applyHeldSpanOperations(working, pendingSpanOperations.get(working.paragraphIndex) ?? [], seamConfirmations);
                pendingSpanOperations.delete(working.paragraphIndex);
                working.text = "";
                continue;
            }
            if (operation.op === "replace_paragraph") {
                /**
                 * WHEN this paragraph has span operations waiting
                 *   CONSEQUENCE
                 *     they are applied first, so a rewrite of the whole paragraph still
                 *     lands after the operations the model listed before it.
                 */
                EditOperations.applyHeldSpanOperations(working, pendingSpanOperations.get(working.paragraphIndex) ?? [], seamConfirmations);
                pendingSpanOperations.delete(working.paragraphIndex);
                const replacementText = EditOperations.requireWordsToWrite(operation.text, operation.op, operationIndex);
                EditOperations.askOnceWhenTextRetypesDocumentMarks(working.text, replacementText, operation.op, operationIndex, seamConfirmations);
                /** The whole paragraph is the span a `replace_paragraph` replaces: 0 to its last character's end. */
                EditOperations.recordLinkInstructions(working, working.text, { start: 0, end: working.text.length }, operation, replacementText, operationIndex, seamConfirmations);
                EditOperations.askOnceWhenTextWritesAnAddressAsWords(working.text, replacementText, operation, operationIndex, seamConfirmations);
                working.text = replacementText;
                continue;
            }
            /**
             * The operation is not applied here. Every span operation of a paragraph is
             * held until the list has been read, then all of them are resolved against
             * the paragraph's text as the model saw it and applied from the end backwards.
             */
            const pendingForParagraph = pendingSpanOperations.get(working.paragraphIndex) ?? [];
            pendingForParagraph.push({ operation, operationIndex, redirectedFromBookmark });
            pendingSpanOperations.set(working.paragraphIndex, pendingForParagraph);
        }
        EditOperations.copyHeadingEditsOntoLinkedEntries(input.paragraphs, workingParagraphs, pendingSpanOperations, tableOfContentsRedirects, seamConfirmations);
        for (const [paragraphIndex, pending] of pendingSpanOperations) {
            EditOperations.applyHeldSpanOperations(workingParagraphs.get(paragraphIndex)!, pending, seamConfirmations);
        }
        const touchedParagraphs = [...workingParagraphs.values()]
            .sort((left, right) => left.paragraphIndex - right.paragraphIndex)
            .map((working) => ({ bookmark: working.bookmark, textProposed: working.text, resolvedAnchors: working.resolvedAnchors, linkInstructions: working.linkInstructions }));
        return { paragraphs: touchedParagraphs, insertions, tableOfContentsRedirects };
    }

    /**
     * Copies the anchored span operations held for a heading onto every line of a
     * linked contents list that lists it, so the list reads as the heading does
     * once the edit is accepted. A field's entries get nothing: Word rewrites them.
     *
     * CASES
     *   - the line's heading holds no anchored span operation      -> nothing copied
     *   - the line has no paragraph bookmark                        -> nothing copied: the apply could not name it
     *   - the line was itself named by an operation                 -> nothing copied: its operations were moved to the heading
     *   - an operation's anchor is not among the line's words       -> the line is left as it reads; the heading's edit stands
     *
     * WHY only anchored operations
     *   An appended sentence belongs after the heading's words, not after the
     *   line's page number; a whole-paragraph replacement describes the heading's
     *   line, which the list's line must not become.
     *
     * EXAMPLE
     *   heading "6. Governing Law" with replace_span "Governing Law" -> "Applicable Law"
     *   line    "6. Governing Law\t12", listed by a linked list
     *   -> the line reads "6. Applicable Law\t12" and the result records the line and the heading as one move
     */
    private static copyHeadingEditsOntoLinkedEntries(
        paragraphs: DocumentParagraph[],
        workingParagraphs: Map<number, WorkingParagraph>,
        pendingSpanOperations: Map<number, PendingSpanOperation[]>,
        redirects: TableOfContentsRedirect[],
        seamConfirmations: SeamConfirmations | null,
    ): void {
        /**
         * WHEN no paragraph has a span operation waiting
         *   CONSEQUENCE
         *     no heading has an edit to copy, so no line of a list is read.
         */
        if (pendingSpanOperations.size === 0) return;
        const paragraphByBookmarkName = EditOperations.paragraphByBookmarkNameOf(paragraphs);
        for (const line of paragraphs) {
            const listing = line.tableOfContentsEntry;
            if (listing === null || listing.listedBy !== "linked_list" || listing.targetParagraphBookmarkName === null || line.paragraphBookmarkName === null) continue;
            if (workingParagraphs.has(line.paragraphIndex)) continue;
            const heading = paragraphByBookmarkName.get(listing.targetParagraphBookmarkName);
            if (heading === undefined) continue;
            const anchoredOperations = (pendingSpanOperations.get(heading.paragraphIndex) ?? []).filter((pending) => SPAN_OPERATIONS.has(pending.operation.op));
            if (anchoredOperations.length === 0) continue;
            const lineWorking: WorkingParagraph = {
                bookmark: line.paragraphBookmarkName,
                paragraphIndex: line.paragraphIndex,
                text: line.allChangesAcceptedText,
                textWithChangesRejected: line.allChangesRejectedText,
                resolvedAnchors: [],
                textAsShown: line.allChangesAcceptedText,
                linksAsShown: line.links,
                linkInstructions: [],
            };
            try {
                EditOperations.applyPendingSpanOperations(lineWorking, anchoredOperations.map((pending) => ({ ...pending, redirectedFromBookmark: null })), seamConfirmations);
            } catch (reask) {
                if (reask instanceof EditOperationReask) continue;
                throw reask;
            }
            /**
             * WHY the line reports no resolved anchors
             *   The model is told where each operation landed once, in the heading's words;
             *   the line's copy is not a second landing to report.
             */
            lineWorking.resolvedAnchors = [];
            workingParagraphs.set(line.paragraphIndex, lineWorking);
            EditOperations.recordRedirect(redirects, line.paragraphBookmarkName, heading.paragraphBookmarkName ?? listing.targetParagraphBookmarkName);
        }
    }

    /**
     * Each paragraph of the section by its paragraph bookmark name, built once so
     * every line of a linked contents list finds its heading in one lookup instead
     * of a scan of the section per line.
     *
     * EDGE CASE
     *   Two paragraphs holding one name: the first keeps it, the paragraph a scan
     *   from the top of the section finds.
     *
     * NULL
     *   a paragraph with no bookmark name is left out; no line can name it.
     */
    private static paragraphByBookmarkNameOf(paragraphs: DocumentParagraph[]): Map<string, DocumentParagraph> {
        const paragraphByBookmarkName = new Map<string, DocumentParagraph>();
        for (const sectionParagraph of paragraphs) {
            if (sectionParagraph.paragraphBookmarkName === null || paragraphByBookmarkName.has(sectionParagraph.paragraphBookmarkName)) continue;
            paragraphByBookmarkName.set(sectionParagraph.paragraphBookmarkName, sectionParagraph);
        }
        return paragraphByBookmarkName;
    }

    /**
     * Adds a move to the result once: a second operation moved off the same entry
     * onto the same heading adds nothing, while a second entry listing the same
     * heading is its own move, because each entry is a paragraph the model named.
     */
    private static recordRedirect(redirects: TableOfContentsRedirect[], entryBookmark: string, headingBookmark: string): void {
        const alreadyRecorded = redirects.some((redirect) => redirect.entryBookmark === entryBookmark && redirect.headingBookmark === headingBookmark);
        if (!alreadyRecorded) redirects.push({ entryBookmark, headingBookmark });
    }

    /**
     * `applyPendingSpanOperations` for one paragraph, with a re-ask of an operation
     * that was moved onto this heading restated first, so the model learns why the
     * text quoted back to it is the heading's and not the entry's it named.
     */
    private static applyHeldSpanOperations(working: WorkingParagraph, pending: PendingSpanOperation[], seamConfirmations: SeamConfirmations | null): void {
        try {
            EditOperations.applyPendingSpanOperations(working, pending, seamConfirmations);
        } catch (reask) {
            if (!(reask instanceof EditOperationReask)) throw reask;
            /**
             * WHEN the operation handed back named this heading itself
             *   CONSEQUENCE
             *     the re-ask stands as written; only a moved operation needs the move explained.
             */
            const redirectedFromBookmark = pending.find((entry) => entry.operationIndex === reask.operationIndex)?.redirectedFromBookmark ?? null;
            if (redirectedFromBookmark === null) throw reask;
            throw new EditOperationReask(
                reask.operationIndex,
                reask.reason,
                `paragraph_id "${redirectedFromBookmark}" is a table of contents entry, so the operation was applied to the heading it lists, paragraph_id "${working.bookmark}". ${reask.message}`,
            );
        }
    }

    /**
     * The running text for `paragraphId`, opened on first touch from the
     * paragraph whose bookmark names carry that id. `insert_paragraph_after`
     * also opens it, so the paragraph it anchors to is checked to exist even
     * though its text is not changed.
     *
     * The operation's id is resolved to a paragraph before the open running
     * texts are consulted, and they are held under `paragraphIndex`, because a
     * paragraph answers to every name in its `bookmarkNames` and two operations
     * may reach one paragraph under two different names — a paragraph the user
     * merged in Word between the naming pass and the apply carries the
     * `_dioptra_p_*` bookmark of each paragraph it was made from.
     *
     * WHEN two operations name one paragraph by two of its bookmarks
     *   CONSEQUENCE
     *     the second continues from the first's running text, as it would under
     *     one name; keying on the name instead would open a second running copy
     *     from the unedited paragraph and each copy would lose the other's edit.
     */
    private static workingParagraphFor(
        operation: ParagraphEditOperation,
        paragraphs: DocumentParagraph[],
        workingParagraphs: Map<number, WorkingParagraph>,
        operationIndex: number,
    ): ResolvedOperationTarget {
        const paragraphId = operation.paragraph_id;
        const paragraph = paragraphs.find((candidate) => candidate.bookmarkNames.includes(paragraphId));
        /**
         * WHEN no paragraph carries the bookmark the operation names
         *   CONSEQUENCE
         *     ask again:the model invented or mistyped a paragraph_id, and it must pick one from the section it was shown.
         */
        if (paragraph === undefined) {
            throw new EditOperationReask(
                operationIndex,
                "unknownParagraph",
                `edit_ops[${operationIndex}]: paragraph_id "${paragraphId}" is not a paragraph of this section — use one of the paragraph_id values shown in the section.`,
            );
        }
        /**
         * WHEN the operation names a table of contents entry
         *   CONSEQUENCE
         *     it is moved to the heading the entry lists, or handed back with that heading named.
         *
         * WHEN it names a line of a linked contents list whose heading the reader could not place
         *   CONSEQUENCE
         *     the line is edited as the paragraph it is: nothing rewrites it, so the edit stands.
         */
        const entry = paragraph.tableOfContentsEntry;
        const linkedLineWithNoHeading = entry !== null && entry.listedBy === "linked_list" && entry.targetParagraphBookmarkName === null;
        if (entry !== null && !linkedLineWithNoHeading) {
            return EditOperations.workingParagraphForTableOfContentsEntry(operation, entry, paragraphs, workingParagraphs, operationIndex);
        }
        return { working: EditOperations.openWorkingParagraph(paragraphId, paragraph, workingParagraphs), redirectedFromBookmark: null };
    }

    /**
     * The running text for an operation that names a table of contents entry: the
     * heading the entry lists, or a re-ask.
     *
     * CASES
     *   - a span operation, and the entry lists a heading in `paragraphs`    -> the heading's running text,
     *                                                                           remembering the entry it came from
     *   - a whole-paragraph operation (replace, delete, append, insert after) -> handed back, naming the heading's
     *                                                                           paragraph_id and words
     *   - the entry lists no heading the list carries                          -> handed back
     *
     * WHY span operations move and whole-paragraph operations do not
     *   A quoted span names words, and the same words read in the heading the entry
     *   lists ("Governing Law"), so moving the edit keeps what the model meant. A
     *   whole-paragraph operation on an entry describes the entry's own line, dots
     *   and page number included, which no heading should become; the model is told
     *   which paragraph_id to name instead.
     */
    private static workingParagraphForTableOfContentsEntry(
        operation: ParagraphEditOperation,
        entry: TableOfContentsEntry,
        paragraphs: DocumentParagraph[],
        workingParagraphs: Map<number, WorkingParagraph>,
        operationIndex: number,
    ): ResolvedOperationTarget {
        const headingBookmark = entry.targetParagraphBookmarkName;
        const heading = headingBookmark === null ? undefined : paragraphs.find((candidate) => candidate.paragraphBookmarkName === headingBookmark);
        /**
         * CASES
         *   - listed by a field       -> Word rewrites the entry on update, so an edit written there is lost
         *   - listed by a linked list -> nothing rewrites the entry, so the heading is edited and the entry follows
         */
        const entryDescription = entry.listedBy === "field"
            ? `edit_ops[${operationIndex}] (${operation.op}): paragraph_id "${operation.paragraph_id}" is a table of contents entry, which Word rewrites from the headings whenever the table updates, so an edit written there is lost.`
            : `edit_ops[${operationIndex}] (${operation.op}): paragraph_id "${operation.paragraph_id}" is a line of a contents list that links to its heading, so the edit is written on the heading and copied onto the line.`;
        if (headingBookmark === null || heading === undefined) {
            throw new EditOperationReask(operationIndex, "tableOfContentsEntry", `${entryDescription} Name the heading or the clause it lists instead, by their paragraph_id.`);
        }
        if (!SPAN_OPERATIONS.has(operation.op)) {
            throw new EditOperationReask(
                operationIndex,
                "tableOfContentsEntry",
                `${entryDescription} It lists the heading paragraph_id "${headingBookmark}", which reads "${EditOperations.preview(heading.allChangesAcceptedText)}". Name that heading, or the paragraphs under it, instead.`,
            );
        }
        return { working: EditOperations.openWorkingParagraph(headingBookmark, heading, workingParagraphs), redirectedFromBookmark: operation.paragraph_id };
    }

    /**
     * The running text of one paragraph, opened on first touch under `bookmark`, the
     * name its desired text is reported under.
     *
     * WHEN an earlier operation already touched this paragraph, under this name or another of its names
     *   CONSEQUENCE
     *     continue from its running text, so operations see each other's results.
     */
    private static openWorkingParagraph(bookmark: string, paragraph: DocumentParagraph, workingParagraphs: Map<number, WorkingParagraph>): WorkingParagraph {
        const alreadyOpen = workingParagraphs.get(paragraph.paragraphIndex);
        if (alreadyOpen !== undefined) return alreadyOpen;
        const opened: WorkingParagraph = {
            bookmark,
            paragraphIndex: paragraph.paragraphIndex,
            text: paragraph.allChangesAcceptedText,
            textWithChangesRejected: paragraph.allChangesRejectedText,
            resolvedAnchors: [],
            textAsShown: paragraph.allChangesAcceptedText,
            linksAsShown: paragraph.links,
            linkInstructions: [],
        };
        workingParagraphs.set(paragraph.paragraphIndex, opened);
        return opened;
    }

    /**
     * Applies every span operation held for one paragraph, all resolved against the
     * paragraph as the model was shown it.
     *
     * WHY the anchors are resolved before any of them is applied
     *   Applied one after another, each operation's anchor had to match the text the
     *   operations before it had already changed — text the model never saw and could
     *   only predict. A quote written for the paragraph as shown was then handed back for
     *   naming words a sibling operation had just removed. Resolving first means every
     *   quote is answered against the text it was copied from.
     *
     * WHY they are then applied last-first
     *   A splice changes the text from its own start onwards. Taken from the end
     *   backwards, every span still to be applied lies before the one just written, so
     *   its offsets are still the offsets that were resolved.
     *
     * `append_sentence` carries no anchor and always lands at the paragraph's end, so it
     * is applied after the splices, in the order the model listed them.
     */
    private static applyPendingSpanOperations(working: WorkingParagraph, pending: PendingSpanOperation[], seamConfirmations: SeamConfirmations | null): void {
        const anchoredOperations = pending.filter((entry) => entry.operation.op !== "append_sentence");
        const appendOperations = pending.filter((entry) => entry.operation.op === "append_sentence");
        const paragraphAsTheModelSawIt = working.text;
        /**
         * WHEN a quote does not answer the paragraph as the model saw it
         *   CONSEQUENCE
         *     every operation of this paragraph is applied the older way instead, one
         *     after another against the running text. That is the plan whose later
         *     operation deliberately quotes what an earlier one wrote — an appended
         *     sentence, a phrase just inserted — which no reading of the original text
         *     can answer. Resolving first is an improvement for the ordinary plan, never
         *     a new re-ask for that one. A plan that also names an `occurrence` is asked
         *     about instead (`askAgainWhenAnOccurrenceCannotBeKept`): its count is of the
         *     paragraph as shown, which the running text no longer is.
         */
        let resolved: Array<{ entry: PendingSpanOperation; anchorSpan: AnchorSpan }>;
        try {
            resolved = anchoredOperations.map((entry) => ({
                entry,
                anchorSpan: EditOperations.locateOperationSpan(
                    paragraphAsTheModelSawIt,
                    entry.operation as Extract<ParagraphEditOperation, { op: "insert_after_anchor" | "insert_before_anchor" | "replace_span" | "delete_span" }>,
                    entry.operationIndex,
                    working.textWithChangesRejected,
                ),
            }));
        } catch (resolutionReask) {
            if (!(resolutionReask instanceof EditOperationReask)) throw resolutionReask;
            EditOperations.askAgainWhenAnOccurrenceCannotBeKept(anchoredOperations, resolutionReask);
            for (const { operation, operationIndex } of pending) EditOperations.applySpanOperation(working, operation, operationIndex, seamConfirmations);
            return;
        }
        /**
         * EXAMPLE
         *   spans { start: 40, end: 52 } and { start: 4, end: 19 }
         *   sorted by start descending -> 40 first, then 4
         *   writing at 40 cannot move offset 4, so the second is still correct
         */
        const inWritingOrder = [...resolved].sort((left, right) => right.anchorSpan.start - left.anchorSpan.start);
        EditOperations.askAgainWhenSpansOverlap(inWritingOrder);
        /**
         * WHY the link instructions are read before any splice
         *   Every span is resolved against the paragraph as the model saw it, the text the
         *   reader's link offsets index; once the first splice is written the running text
         *   no longer does.
         */
        for (const { entry, anchorSpan } of resolved) {
            EditOperations.recordLinkInstructionsOfSpanOperation(working, paragraphAsTheModelSawIt, anchorSpan, entry.operation, entry.operationIndex, seamConfirmations);
        }
        for (const { entry, anchorSpan } of inWritingOrder) {
            /**
             * The characters the paragraph actually holds where this operation landed,
             * recorded before the splice removes them. A quote the model retyped — a
             * typewriter apostrophe, a regional spelling — is answered here with the
             * document's own wording, so the next call quotes what is really there
             * instead of guessing at the same spelling again.
             */
            working.resolvedAnchors.push({ operationIndex: entry.operationIndex, documentText: working.text.slice(anchorSpan.start, anchorSpan.end) });
            working.text = EditOperations.spliceAtAnchor(
                working.text,
                anchorSpan,
                entry.operation as Extract<ParagraphEditOperation, { op: "insert_after_anchor" | "insert_before_anchor" | "replace_span" | "delete_span" }>,
                entry.operationIndex,
                seamConfirmations,
            );
        }
        for (const { operation, operationIndex } of appendOperations) {
            working.text = EditOperations.appendAsWritten(working.text, operation as Extract<ParagraphEditOperation, { op: "append_sentence" }>, operationIndex, seamConfirmations);
        }
    }

    /**
     * Asks the model again for a paragraph whose operations would be applied one after
     * another while one of them names an `occurrence`, which is counted in the paragraph
     * as the model saw it.
     *
     * WHY
     *   On the running text the count lands on another stretch: once occurrence 1 is
     *   written, the paragraph's second "Supplier" is the first one left, and occurrence
     *   2 replaces the third. A count made on the paragraph as shown is kept only by
     *   resolving every quote against that paragraph.
     *
     * CASES
     *   - no operation of the paragraph names an occurrence  -> nothing to keep: the
     *                                                           paragraph is applied one
     *                                                           after another
     *   - the operation whose quote did not answer the
     *     paragraph as shown names an occurrence itself      -> its own re-ask stands
     *                                                           (`occurrenceMissing`)
     *   - another operation's quote did not answer it        -> the first operation that
     *                                                           names an occurrence is asked
     *                                                           again, naming that quote
     *
     * EXAMPLE
     *   "Supplier pays. The Supplier delivers. Supplier warrants."
     *   edit_ops[1] replace_span "Supplier" occurrence 1, edit_ops[2] occurrence 2,
     *   edit_ops[3] insert_after_anchor "Acme Widgets pays", words edit_ops[1] writes
     *   -> edit_ops[1] asked again (`occurrenceBesideWrittenText`), naming edit_ops[3]
     */
    private static askAgainWhenAnOccurrenceCannotBeKept(anchoredOperations: PendingSpanOperation[], resolutionReask: EditOperationReask): void {
        const operationsNamingAnOccurrence = anchoredOperations.filter((entry) => entry.operation.op === "replace_span" && entry.operation.occurrence !== undefined);
        if (operationsNamingAnOccurrence.length === 0) return;
        if (operationsNamingAnOccurrence.some((entry) => entry.operationIndex === resolutionReask.operationIndex)) throw resolutionReask;
        const firstNamingAnOccurrence = operationsNamingAnOccurrence[0];
        throw new EditOperationReask(
            firstNamingAnOccurrence.operationIndex,
            "occurrenceBesideWrittenText",
            `edit_ops[${firstNamingAnOccurrence.operationIndex}] (replace_span): "occurrence" counts the anchor in the paragraph as shown, but edit_ops[${resolutionReask.operationIndex}] in the same paragraph quotes words the paragraph as shown does not hold, so the count cannot be kept. Quote the paragraph as shown in edit_ops[${resolutionReask.operationIndex}], or name this span by words that occur once in the paragraph instead of "occurrence".`,
        );
    }

    /**
     * Asks the model again for a plan whose operations name spans of one paragraph that meet.
     *
     * WHEN two spans overlap, or one ends exactly where the next begins
     *   CONSEQUENCE
     *     ask again:applied together their results depend on which was written first, and
     *     the checks at each join (`askAgainWhenJoinsGlueOrDouble`) read the paragraph's
     *     character beside the cut, which a touching span would already have rewritten.
     *     Touching counts as meeting for that reason, not only true overlap.
     *
     * `inWritingOrder` is sorted by `start` descending, so `later` is the span to the
     * right of `earlier` and `earlier.end` is what must stay clear of `later.start`.
     */
    private static askAgainWhenSpansOverlap(inWritingOrder: Array<{ entry: PendingSpanOperation; anchorSpan: AnchorSpan }>): void {
        for (let position = 0; position + 1 < inWritingOrder.length; position += 1) {
            const later = inWritingOrder[position];
            const earlier = inWritingOrder[position + 1];
            if (earlier.anchorSpan.end < later.anchorSpan.start) continue;
            const operationIndex = Math.max(later.entry.operationIndex, earlier.entry.operationIndex);
            throw new EditOperationReask(
                operationIndex,
                "anchorOverlap",
                `edit_ops[${operationIndex}] (${later.entry.operation.op}): this operation's anchor meets the anchor of edit_ops[${Math.min(later.entry.operationIndex, earlier.entry.operationIndex)}] in the same paragraph — give each operation its own span, or send one operation that covers both.`,
            );
        }
    }

    /**
     * The four operations that edit inside a paragraph's text, each written
     * as: locate the anchor (or the end), cut, and write the text in exactly as
     * given once its joins pass (`askAgainWhenJoinsGlueOrDouble`), a replacement's
     * retyped characters in the paragraph's own form (`spliceAtAnchor`). The
     * working paragraph's text is replaced with the result.
     */
    private static applySpanOperation(
        working: WorkingParagraph,
        operation: Extract<ParagraphEditOperation, { op: "append_sentence" | "insert_after_anchor" | "insert_before_anchor" | "replace_span" | "delete_span" }>,
        operationIndex: number,
        seamConfirmations: SeamConfirmations | null,
    ): void {
        const text = working.text;
        /**
         * WHEN the operation appends a sentence
         *   CONSEQUENCE
         *     no anchor to find: the text goes after the paragraph's last character; every
         *     reference span sits before the end, so none moves.
         */
        if (operation.op === "append_sentence") {
            working.text = EditOperations.appendAsWritten(text, operation, operationIndex, seamConfirmations);
            return;
        }
        const anchorSpan = EditOperations.locateOperationSpan(text, operation, operationIndex, working.textWithChangesRejected);
        EditOperations.recordLinkInstructionsOfSpanOperation(working, text, anchorSpan, operation, operationIndex, seamConfirmations);
        working.text = EditOperations.spliceAtAnchor(text, anchorSpan, operation, operationIndex, seamConfirmations);
    }

    /**
     * The paragraph with an `append_sentence` written onto its end exactly as given,
     * once its words and its join pass. The paragraph's own trailing characters stay.
     */
    private static appendAsWritten(paragraphText: string, operation: Extract<ParagraphEditOperation, { op: "append_sentence" }>, operationIndex: number, seamConfirmations: SeamConfirmations | null): string {
        const wordsAsGiven = EditOperations.requireWordsWrittenInParagraph(operation.text, operation.op, operationIndex, seamConfirmations);
        const paragraphAsWritten = EditOperations.appendedAsWritten(paragraphText, wordsAsGiven);
        EditOperations.askAgainWhenJoinsGlueOrDouble(paragraphAsWritten, operation.op, operationIndex, seamConfirmations);
        EditOperations.askOnceWhenTextRetypesDocumentMarks(paragraphText, paragraphAsWritten.text, operation.op, operationIndex, seamConfirmations);
        EditOperations.askOnceWhenTextWritesAnAddressAsWords(paragraphText, wordsAsGiven, operation, operationIndex, seamConfirmations);
        return paragraphAsWritten.text;
    }

    /**
     * The text with one anchored operation applied at `anchorSpan`, its words written
     * exactly as given once they and their joins pass.
     *
     * CASES, by the words the operation writes
     *   - `delete_span`          -> none; only the join where the two sides close is checked
     *   - `replace_span`         -> the text as given, handed back when empty or when it starts or
     *                               ends with a line break, then checked at both its joins; a
     *                               word it retypes in another form is the paragraph's word to
     *                               the engine that writes it (`TextEditPlanner`)
     *   - the two insertions     -> the text as given, handed back and checked the same way
     *
     * RETURNS
     *   The text it leaves.
     */
    private static spliceAtAnchor(
        text: string,
        anchorSpan: AnchorSpan,
        operation: Extract<ParagraphEditOperation, { op: "insert_after_anchor" | "insert_before_anchor" | "replace_span" | "delete_span" }>,
        operationIndex: number,
        seamConfirmations: SeamConfirmations | null,
    ): string {
        const wordsAsGiven = operation.op === "delete_span" ? "" : EditOperations.requireWordsWrittenInParagraph(operation.text, operation.op, operationIndex, seamConfirmations);
        const paragraphAsWritten = EditOperations.splicedAsWritten(text, anchorSpan, operation, wordsAsGiven);
        EditOperations.askAgainWhenJoinsGlueOrDouble(paragraphAsWritten, operation.op, operationIndex, seamConfirmations);
        EditOperations.askOnceWhenTextRetypesDocumentMarks(text, paragraphAsWritten.text, operation.op, operationIndex, seamConfirmations);
        EditOperations.askOnceWhenTextWritesAnAddressAsWords(text, wordsAsGiven, operation, operationIndex, seamConfirmations);
        return paragraphAsWritten.text;
    }

    /**
     * Where the anchor sits in the accepted text. The anchor is read the way
     * the model saw the paragraph: XML entities and `<br/>` are undone first,
     * because the prompt shows `&lt;5%` and `<br/>` where the accepted text
     * holds `<5%` and a line break. The match must be unique; a repeated
     * phrase is handed back with the instruction to quote more.
     *
     * `textWithChangesRejected` is the paragraph before its tracked changes, read
     * only when the anchor is missing, to say whether the model anchored on struck
     * words.
     */
    private static locateAnchor(text: string, anchor: string, operationName: string, operationIndex: number, textWithChangesRejected: string): AnchorSpan {
        const anchorAsAcceptedText = EditOperations.promptTextToAcceptedText(anchor);
        /**
         * WHEN the anchor is blank
         *   CONSEQUENCE
         *     ask again:there is nothing to look for.
         */
        if (anchorAsAcceptedText.trim().length === 0) {
            throw new EditOperationReask(operationIndex, "emptyAnchor", `edit_ops[${operationIndex}] (${operationName}): anchor is empty — quote the exact span from the paragraph's current text.`);
        }
        /**
         * CASES, in the order they are tried
         *   - the quote is character-for-character in the paragraph -> that span
         *   - it differs only in characters a reader cannot see     -> that span, found by folding
         *   - neither                                               -> ask again
         *
         * WHY the second pass exists
         *   A model retypes a quote rather than copying bytes, and the characters it
         *   silently standardises are the ones a contract is full of: the typographic
         *   apostrophe in `Supplier’s`, en and em dashes, non-breaking spaces. The
         *   anchor then names the right words and is handed back over a character nobody
         *   can see. Both passes return offsets into `text`, never into the folded
         *   form, so the edit is spliced from the paragraph's own characters and the
         *   model's spelling of the quote is never written into the document.
         */
        const exactSpan = EditOperations.locateAnchorExactly(text, anchorAsAcceptedText, anchor, operationName, operationIndex);
        if (exactSpan !== null) return exactSpan;
        const foldedSpan = EditOperations.locateAnchorByFolding(text, anchorAsAcceptedText, anchor, operationName, operationIndex);
        if (foldedSpan !== null) return foldedSpan;
        const similarWordsSpan = EditOperations.locateAnchorByWordSimilarity(text, anchorAsAcceptedText, anchor, operationName, operationIndex);
        if (similarWordsSpan !== null) return similarWordsSpan;
        /**
         * WHEN the anchor reads in the paragraph before its tracked changes but not now
         *   CONSEQUENCE
         *     ask again naming the cause: the model anchored on struck words. Struck text
         *     does not exist for an edit; the re-ask restates the current text so the
         *     next answer anchors on it.
         *
         * WHY this is a check in code and not a line in the prompt
         *   The fold's edit model anchored on struck words three times in one run
         *   (2026-09-22) with the prompt already telling it not to; a prompt line is
         *   advice, this re-ask is the rule.
         */
        if (EditOperations.anchorNamesStruckWords(textWithChangesRejected, anchorAsAcceptedText)) {
            throw new EditOperationReask(
                operationIndex,
                "anchorStruck",
                `edit_ops[${operationIndex}] (${operationName}): anchor "${EditOperations.preview(anchor)}" names words that are struck (deleted) in this paragraph. Struck text does not exist and cannot be anchored on. ${EditOperations.anchorOnCurrentTextInstruction(text)}`,
            );
        }
        /**
         * WHEN the anchor is in the paragraph under neither reading, struck or current
         *   CONSEQUENCE
         *     ask again:the model paraphrased, quoted the paragraph as it read when the
         *     instruction was written, or quoted another paragraph. The re-ask restates
         *     the current text, since an out-of-date instruction is the usual cause.
         */
        throw new EditOperationReask(
            operationIndex,
            "anchorMissing",
            `edit_ops[${operationIndex}] (${operationName}): anchor "${EditOperations.preview(anchor)}" does not appear in the paragraph's current text — quote it verbatim, including punctuation and capitalization, then resubmit. ${EditOperations.anchorOnCurrentTextInstruction(text)}`,
        );
    }

    /**
     * Whether an anchor that is missing from the current text reads in the paragraph
     * before its tracked changes, character for character or under the folding the
     * anchor passes go through. The anchor is already known to be missing from the
     * current text, so a hit here means it names at least one struck word; how many
     * times it hits does not matter.
     *
     * EXAMPLE
     *   current text (changes accepted) = "Fees are payable net sixty days."
     *   before changes (rejected)       = "Fees are payable net thirty days."
     *   anchor "net thirty days"        -> true: "thirty" is struck
     *   anchor "net ninety days"        -> false: never in the paragraph
     */
    private static anchorNamesStruckWords(textWithChangesRejected: string, anchorAsAcceptedText: string): boolean {
        if (textWithChangesRejected.includes(anchorAsAcceptedText)) return true;
        /** The fold the anchor passes read through (`WordTokenizer.foldForMatching`), without the map back, which a yes-or-no answer never needs. */
        return WordTokenizer.comparisonKey(textWithChangesRejected).includes(WordTokenizer.comparisonKey(anchorAsAcceptedText));
    }

    /**
     * The sentence every missing-anchor re-ask ends with: the paragraph's current
     * text, so the model anchors on what the paragraph reads now rather than on the
     * instruction's out-of-date quote. The whole text is given, since the anchor must
     * be a span of it.
     */
    private static anchorOnCurrentTextInstruction(text: string): string {
        return `Anchor on the paragraph's current text, which reads: "${text}"`;
    }

    /**
     * The span an operation acts on, however the operation named it.
     *
     * CASES
     *   - `anchor`      -> the whole span quoted in one piece, start to end
     *   - `from` + `to` -> the span's two ends quoted separately, everything between
     *                      them included
     *
     * WHY the pair exists
     *   `replace_span` and `delete_span` consume what they name, so a one-piece quote is
     *   the whole replaced text: rewriting a liability sentence meant reproducing 165
     *   characters exactly, and one drifting character sent the step back to the model. Quoting three
     *   words at each end is the same span named with a fraction of the characters, and
     *   each end has only to be unique within the paragraph rather than the whole quote.
     *
     * EXAMPLE
     *   text = "6.1 Limitation. IN NO EVENT SHALL Supplier'S LIABILITY EXCEED THE FEES PAID."
     *   from = "IN NO EVENT",  to = "THE FEES PAID"
     *   from resolves to { start: 16, end: 27 }, to resolves to { start: 61, end: 74 }
     *   span -> { start: 16, end: 74 }, which covers both ends and everything between
     */
    private static locateOperationSpan(
        text: string,
        operation: Extract<ParagraphEditOperation, { op: "insert_after_anchor" | "insert_before_anchor" | "replace_span" | "delete_span" }>,
        operationIndex: number,
        textWithChangesRejected: string,
    ): AnchorSpan {
        /**
         * WHEN a `replace_span` names which occurrence of its quote it replaces
         *   CONSEQUENCE
         *     that occurrence: a change made throughout the document names each of several
         *     identical words in a paragraph this way, where the quote alone is ambiguous.
         */
        if (operation.op === "replace_span" && operation.anchor !== undefined && operation.occurrence !== undefined) {
            return EditOperations.locateNthOccurrence(text, operation.anchor, operation.occurrence, operationIndex);
        }
        if (operation.anchor !== undefined) return EditOperations.locateAnchor(text, operation.anchor, operation.op, operationIndex, textWithChangesRejected);
        /**
         * `insert_after_anchor` has no `from`/`to` in its schema at all, so the pair is
         * read off the two operations that take one and is undefined for that third.
         */
        const spanEnds = operation as Extract<ParagraphEditOperation, { op: "replace_span" | "delete_span" }>;
        const spanStart = spanEnds.from;
        const spanEnd = spanEnds.to;
        /**
         * WHEN the operation names neither a whole quote nor both ends
         *   CONSEQUENCE
         *     ask again:there is no span to act on. `insert_after_anchor` never takes a pair,
         *     since it marks a point rather than a stretch of text.
         */
        if (spanStart === undefined || spanEnd === undefined || operation.op === "insert_after_anchor" || operation.op === "insert_before_anchor") {
            throw new EditOperationReask(
                operationIndex,
                "emptyAnchor",
                `edit_ops[${operationIndex}] (${operation.op}): name the span either as "anchor", the whole span quoted once, or as "from" and "to", a quote of each end.`,
            );
        }
        const startSpan = EditOperations.locateAnchor(text, spanStart, operation.op, operationIndex, textWithChangesRejected);
        const endSpan = EditOperations.locateAnchor(text, spanEnd, operation.op, operationIndex, textWithChangesRejected);
        /**
         * WHEN `to` resolves before `from` ends
         *   CONSEQUENCE
         *     ask again:the two quotes do not name a stretch of text in the order given, so
         *     the span would run backwards. Both ends were found exactly once, so the
         *     same two quotes in the other order name a real span; the re-ask offers
         *     that pair, and the model has only to confirm it.
         *
         * EXAMPLE
         *   text = "Either party may terminate upon sixty (60) days prior written notice."
         *   from = "prior written notice"  -> { start: 48, end: 68 }
         *   to   = "Either party"           -> { start: 0, end: 12 }
         *   12 <= 48, so the span runs backwards; the offer is from "Either party" to "prior written notice"
         */
        if (endSpan.end <= startSpan.start) {
            throw new EditOperationReask(
                operationIndex,
                "spanReversed",
                `edit_ops[${operationIndex}] (${operation.op}): "to" appears before "from" in the paragraph. Did you mean from "${EditOperations.preview(spanEnd)}" to "${EditOperations.preview(spanStart)}"? Quote the span's ends in the order they read, then resubmit.`,
            );
        }
        return { start: startSpan.start, end: endSpan.end };
    }

    /**
     * The anchor's span when the paragraph holds it character for character.
     *
     * RETURNS
     *   the span, or null when the quote is not in the paragraph under this reading
     *   so the caller can try the next one.
     *
     * THROWS
     *   when the quote is in the paragraph more than once: applying it would guess
     *   which occurrence the model meant, and that stays a re-ask however the quote
     *   was found. A match inside a longer word counts as an occurrence, so
     *   "reasonably withheld" in a paragraph that also reads "unreasonably withheld"
     *   is handed back and the model quotes a word more.
     *
     * EXAMPLE
     *   text   = "Fees are due within thirty (30) days of invoice."
     *   anchor = "thirty (30) days"
     *   one match at 20 -> { start: 20, end: 36 }
     */
    private static locateAnchorExactly(text: string, anchorAsAcceptedText: string, anchor: string, operationName: string, operationIndex: number): AnchorSpan | null {
        const matchStarts = EditOperations.matchStartsOf(text, anchorAsAcceptedText);
        if (matchStarts.length === 0) return null;
        /**
         * WHEN the quote matches in more than one place
         *   CONSEQUENCE
         *     ask again as ambiguous, showing the first two matches.
         */
        if (matchStarts.length > 1) {
            throw EditOperations.ambiguousAnchorReask(anchor, operationName, operationIndex, {
                text,
                spans: matchStarts.slice(0, 2).map((matchStart) => ({ start: matchStart, end: matchStart + anchorAsAcceptedText.length })),
            });
        }
        const [onlyMatchStart] = matchStarts;
        /**
         * EXAMPLE
         *   onlyMatchStart               = 20
         *   anchorAsAcceptedText.length  = 16
         *   end                          = 36, one past the anchor's last character
         */
        return { start: onlyMatchStart, end: onlyMatchStart + anchorAsAcceptedText.length };
    }

    /**
     * Every place `quote` starts in `text`, in reading order, matches that overlap
     * included.
     *
     * EXAMPLE
     *   text  = "Notice is required. Notice is required."
     *   quote = "Notice is required."
     *   -> [0, 20]
     *   each search resumes at `previous start + 1`: 0 + 1 = 1, so the next match
     *   may start anywhere after the previous one's first character
     */
    private static matchStartsOf(text: string, quote: string): number[] {
        const matchStarts: number[] = [];
        let matchStart = text.indexOf(quote);
        while (matchStart !== -1) {
            matchStarts.push(matchStart);
            matchStart = text.indexOf(quote, matchStart + 1);
        }
        return matchStarts;
    }

    /**
     * The anchor's span when it matches the paragraph once every character a reader
     * cannot tell apart is folded together: the typographic quotes to the typewriter
     * ones, every dash to a hyphen, every run of whitespace to one space, and every
     * accent stored as its own character composed onto its letter.
     *
     * RETURNS
     *   the span **in the paragraph's own characters**, or null when the folded quote
     *   is not in the folded paragraph. The fold is only ever a way of finding the
     *   words; the offsets handed back index `text`, so the splice that follows cuts
     *   the document's characters and not the model's retyping of them.
     *
     * EXAMPLE
     *   text   = "IN NO EVENT SHALL Supplier\u2019S LIABILITY EXCEED THE FEES PAID."
     *   anchor = "IN NO EVENT SHALL Supplier'S LIABILITY"      (typewriter apostrophe)
     *   folded text   = "IN NO EVENT SHALL Supplier'S LIABILITY EXCEED THE FEES PAID."
     *   folded anchor = "IN NO EVENT SHALL Supplier'S LIABILITY"
     *   folded match at 0, length 37 -> source span { start: 0, end: 37 }
     *   the splice then reads `Supplier\u2019S` out of the paragraph, curly apostrophe intact.
     */
    private static locateAnchorByFolding(text: string, anchorAsAcceptedText: string, anchor: string, operationName: string, operationIndex: number): AnchorSpan | null {
        const foldedParagraph = WordTokenizer.foldForMatching(text);
        /**
         * WHY the folded anchor is trimmed
         *   Folding turns a quote's leading or trailing whitespace into a single
         *   space, which would then have to match a space in the paragraph. The ends
         *   of a quote are the model's framing, not part of the words it names.
         */
        const foldedAnchor = WordTokenizer.foldForMatching(anchorAsAcceptedText).text.trim();
        if (foldedAnchor.length === 0) return null;
        const matchStarts = EditOperations.matchStartsOf(foldedParagraph.text, foldedAnchor);
        if (matchStarts.length === 0) return null;
        /**
         * WHEN folding makes the quote match in two places
         *   CONSEQUENCE
         *     ask again, exactly as an ambiguous exact quote does. Forgiving the characters
         *     a reader cannot see must never forgive not knowing which span was meant.
         *     Each match is mapped back so the re-ask quotes the paragraph's characters.
         *
         * EXAMPLE
         *   text          = "The Supplier’s duty and the Supplier’s duty both apply."
         *   folded anchor = "Supplier's duty", length 15, folded matches at 4 and 28
         *   `4 + 15 - 1`  -> 18, the folded index of the first match's last character
         *   spans in `text` = { start: 4, end: 19 } and { start: 28, end: 43 }, each "Supplier’s duty"
         */
        if (matchStarts.length > 1) {
            throw EditOperations.ambiguousAnchorReask(anchor, operationName, operationIndex, {
                text,
                spans: matchStarts.slice(0, 2).map((foldedMatchStart) => ({
                    start: foldedParagraph.sourceStartByFoldedIndex[foldedMatchStart],
                    end: foldedParagraph.sourceEndByFoldedIndex[foldedMatchStart + foldedAnchor.length - 1],
                })),
            });
        }
        const [firstMatch] = matchStarts;
        /**
         * EXAMPLE
         *   foldedAnchor.length = 37, firstMatch = 0
         *   `firstMatch + 37 - 1` -> 36, the folded index of the anchor's LAST character
         *   sourceEndByFoldedIndex[36] is one past that character in `text`
         *
         *   `- 1` because the end map is indexed by character, and character 37 would
         *   be the one after the anchor.
         */
        return {
            start: foldedParagraph.sourceStartByFoldedIndex[firstMatch],
            end: foldedParagraph.sourceEndByFoldedIndex[firstMatch + foldedAnchor.length - 1],
        };
    }

    /**
     * The anchor's span when the paragraph holds the same words in the same order and
     * each of them is spelled near enough to the quote's.
     *
     * WHY a third reading
     *   A contract is written in one regional spelling and a model answers in another:
     *   `organisation`/`organization`, `licence`/`license`, `authorised`/`authorized`.
     *   The words are the same words and the order is the quote's own; only the letters
     *   inside one or two of them differ, which the fold above cannot forgive because it
     *   only folds characters that look alike.
     *
     * WHY the words are the redline diff's units (`WordTokenizer.diffUnits`)
     *   Japanese, Chinese and Thai put no spaces between words, so cut at whitespace a
     *   whole clause was one word, and a word that long may differ by two letters. A
     *   quote naming a term the paragraph no longer reads then answered it, and the
     *   operation rewrote words the model never quoted. Cut into units, each word of
     *   these scripts is held to the rule an English word is held to.
     *
     *   EXAMPLE
     *     paragraph "本製品の保証期間は、納品日から二年間とする。"
     *     quote     "本製品の保証期間は、納品日から一年間とする。"
     *     one whitespace word each, 22 characters, one letter apart -> answered: the
     *     replacement was written over 二年 although the quote named 一年
     *     in units, "二" against "一" is a one-letter word that must match exactly
     *     -> no window answers, and the quote is handed back as missing
     *
     * The order is never relaxed: a window of the paragraph's words matches only if its
     * first word answers the anchor's first word, its second the anchor's second, and so
     * on. Nothing is matched out of order, and nothing is skipped.
     *
     * RETURNS
     *   the span in the paragraph's own characters, or null when no window matches.
     *
     * EXAMPLE
     *   text   = "The Supplier shall obtain authorisation from the organisation."
     *   anchor = "obtain authorization from the organization"
     *   windows tried at every word; the one starting at "obtain" matches word for word,
     *   two of its words at edit distance 1 -> span covers "obtain … organization" as the
     *   document spells it, and the splice writes the document's letters back.
     *
     *   text   = "受託者はコンピューターを納入する。"
     *   anchor = "受託者はコンピュータを納入する"
     *   units  受託 / 者 / は / コンピューター / を / 納入 / する / 。 against the quote's seven;
     *   the window at unit 0 matches, "コンピューター" at edit distance 1 -> span covers
     *   "受託者はコンピューターを納入する", the paragraph's spelling, and stops before "。"
     */
    private static locateAnchorByWordSimilarity(text: string, anchorAsAcceptedText: string, anchor: string, operationName: string, operationIndex: number): AnchorSpan | null {
        const foldedParagraph = WordTokenizer.foldForMatching(text);
        const foldedAnchor = WordTokenizer.foldForMatching(anchorAsAcceptedText).text.trim();
        const paragraphUnits = WordTokenizer.diffUnits(foldedParagraph.text);
        const anchorUnitTexts = WordTokenizer.diffUnits(foldedAnchor).map((anchorUnit) => anchorUnit.text);
        /**
         * WHEN the quote has no words, or more words than the paragraph holds
         *   CONSEQUENCE
         *     no window can match; the caller falls through to the re-ask.
         */
        if (anchorUnitTexts.length === 0 || paragraphUnits.length < anchorUnitTexts.length) return null;
        const windowStartsThatMatch: number[] = [];
        /**
         * EXAMPLE
         *   paragraphUnits.length = 12, anchorUnitTexts.length = 5
         *   the last window that fits starts at 12 - 5 = 7, so the bound is `<=`
         */
        for (let windowStart = 0; windowStart + anchorUnitTexts.length <= paragraphUnits.length; windowStart += 1) {
            const everyWordAnswersItsOwn = anchorUnitTexts.every((anchorUnitText, anchorUnitPosition) => EditOperations.wordsAreTheSameWord(paragraphUnits[windowStart + anchorUnitPosition].text, anchorUnitText));
            if (everyWordAnswersItsOwn) windowStartsThatMatch.push(windowStart);
        }
        if (windowStartsThatMatch.length === 0) return null;
        /**
         * WHEN more than one window answers the quote
         *   CONSEQUENCE
         *     ask again, as both readings above do. A loose reading must never be the one
         *     that guesses which span the model meant. Each window is mapped back so the
         *     re-ask quotes the paragraph's characters and the window's own length.
         *
         * EXAMPLE
         *   text   = "The licence fee’s cap and the licence fee’s floor both apply."
         *   anchor = "license fee's", 2 words; a window starts at word 1, "licence", offset 4
         *   its last word is word `1 + 2 - 1` = 2, "fee's", which ends at folded offset 17
         *   `17 - 1` -> 16, the window's last folded character -> span { start: 4, end: 17 }, "licence fee’s"
         */
        if (windowStartsThatMatch.length > 1) {
            throw EditOperations.ambiguousAnchorReask(anchor, operationName, operationIndex, {
                text,
                spans: windowStartsThatMatch.map((windowStart) => ({
                    start: foldedParagraph.sourceStartByFoldedIndex[paragraphUnits[windowStart].startOffset],
                    end: foldedParagraph.sourceEndByFoldedIndex[paragraphUnits[windowStart + anchorUnitTexts.length - 1].endOffset - 1],
                })),
            });
        }
        const firstUnit = paragraphUnits[windowStartsThatMatch[0]];
        /**
         * EXAMPLE
         *   windowStart = 7, anchorUnitTexts.length = 5
         *   `7 + 5 - 1` -> 11, the LAST unit of the window, not the one after it
         */
        const lastUnit = paragraphUnits[windowStartsThatMatch[0] + anchorUnitTexts.length - 1];
        return {
            start: foldedParagraph.sourceStartByFoldedIndex[firstUnit.startOffset],
            end: foldedParagraph.sourceEndByFoldedIndex[lastUnit.endOffset - 1],
        };
    }

    /**
     * Whether two words are the same word differently spelled.
     *
     * CASES
     *   - the same letters, any case         -> yes ("Supplier" answers "supplier")
     *   - within the distance allowed below  -> yes ("organisation" answers "organization")
     *   - anything further apart             -> no ("thirty" never answers "sixty")
     *
     * WHY the allowance grows with length
     *   A regional spelling changes one or two letters wherever it appears, but on a
     *   short word one letter is the difference between two different words: "may" and
     *   "way", "30" and "90". A word of four letters or fewer must match exactly.
     */
    private static wordsAreTheSameWord(paragraphWord: string, anchorWord: string): boolean {
        const paragraphWordLower = paragraphWord.toLowerCase();
        const anchorWordLower = anchorWord.toLowerCase();
        if (paragraphWordLower === anchorWordLower) return true;
        const shorterLength = Math.min(paragraphWordLower.length, anchorWordLower.length);
        if (shorterLength <= SHORTEST_WORD_THAT_MAY_DIFFER) return false;
        const lettersThatMayDiffer = shorterLength > LONGER_WORD_LENGTH ? 2 : 1;
        if (Math.abs(paragraphWordLower.length - anchorWordLower.length) > lettersThatMayDiffer) return false;
        return EditOperations.editDistanceWithin(paragraphWordLower, anchorWordLower, lettersThatMayDiffer);
    }

    /**
     * Whether `left` can be turned into `right` with at most `allowed` single-character
     * insertions, deletions or substitutions.
     *
     * The row is the usual Levenshtein row, kept bounded: a row whose every entry already
     * exceeds `allowed` can only grow, so the answer is no and the rest is not computed.
     * That keeps this cheap enough to run at every word position of a paragraph.
     */
    private static editDistanceWithin(left: string, right: string, allowed: number): boolean {
        /**
         * EXAMPLE
         *   left = "licence", right = "license"
         *   previousRow starts [0,1,2,3,4,5,6,7], one entry per prefix of `right`
         */
        let previousRow = Array.from({ length: right.length + 1 }, (_entry, rightPrefixLength) => rightPrefixLength);
        for (let leftPosition = 1; leftPosition <= left.length; leftPosition += 1) {
            const currentRow = [leftPosition];
            for (let rightPosition = 1; rightPosition <= right.length; rightPosition += 1) {
                const substitutionCost = left[leftPosition - 1] === right[rightPosition - 1] ? 0 : 1;
                currentRow.push(Math.min(
                    currentRow[rightPosition - 1] + 1,
                    previousRow[rightPosition] + 1,
                    previousRow[rightPosition - 1] + substitutionCost,
                ));
            }
            if (Math.min(...currentRow) > allowed) return false;
            previousRow = currentRow;
        }
        return previousRow[right.length] <= allowed;
    }

    /**
     * The re-ask for a quote that names more than one span, raised the same way
     * whichever pass found it twice: it says which way to grow the quote so the edit
     * point stays put, and shows each match with the words before it so the first
     * retry lands.
     */
    private static ambiguousAnchorReask(anchor: string, operationName: string, operationIndex: number, matches: AmbiguousMatches): EditOperationReask {
        const LEFT_CONTEXT_CHARACTERS = 30;
        /**
         * EXAMPLE
         *   text  = "Notice is required. Notice is required. The end."
         *   spans = [{ start: 0, end: 19 }, { start: 20, end: 39 }]
         *   slice(max(0, 0 - 30), 0)   -> "" before the first match; slice(0, 19) -> "Notice is required."
         *   slice(max(0, 20 - 30), 20) -> "Notice is required. " before the second
         *   -> "[Notice is required.]"; "Notice is required. [Notice is required.]"
         */
        const quotedMatches = matches.spans
            .map((span) => `"${matches.text.slice(Math.max(0, span.start - LEFT_CONTEXT_CHARACTERS), span.start)}[${matches.text.slice(span.start, span.end)}]"`)
            .join("; ");
        return new EditOperationReask(
            operationIndex,
            "anchorAmbiguous",
            `edit_ops[${operationIndex}] (${operationName}): anchor "${EditOperations.preview(anchor)}" appears more than once in the paragraph — ${EditOperations.extendQuoteDirection(operationName)} Matches: ${quotedMatches}`,
        );
    }

    /**
     * How an ambiguous quote grows so the edit still lands where the model meant.
     *
     * CASES
     *   - insert_after_anchor  -> grow to the left: the text goes after the quote's last word, which must not move
     *   - insert_before_anchor -> grow to the right: the text goes before the quote's first word, which must not move
     *   - any other operation  -> either side; the added words become part of the edited span, so they are carried
     *                             in `text`, or the span is named with `from` and `to` instead
     */
    private static extendQuoteDirection(operationName: string): string {
        if (operationName.startsWith("insert_after_anchor")) return "extend the quote to the LEFT, adding the words before it, so its last word (where the text goes) stays the same.";
        if (operationName.startsWith("insert_before_anchor")) return "extend the quote to the RIGHT, adding the words after it, so its first word (where the text goes) stays the same.";
        return 'extend the quote until it matches once; the words you add become part of the edited span, so carry them in "text", or name the span with "from" and "to".';
    }

    /**
     * The characters `paragraphText` holds for the first stretch that reads as
     * `quoteText` under the anchor fold (`foldedForMatching`), so a quote the model
     * retyped with a straight apostrophe, a hyphen for a dash or one space for two
     * names the paragraph's own words.
     *
     * WHY the paragraph's characters, not the quote's
     *   A Word comment is placed by searching Word for the quoted words, character for
     *   character. A quote typed "days' notice" over "days’ notice" was handed back by the
     *   core's add_comment as not in the paragraph (R24, 2026-10-16), and a live-review
     *   comment quoting it sat on the paragraph's first word; written back in the
     *   paragraph's own characters, the quote is found as the anchor of an edit is.
     *
     * NULL
     *   The quote holds nothing but whitespace, or no stretch of the paragraph reads as it.
     *
     * EXAMPLE
     *   paragraph "10.19 Customer may audit Supplier’s compliance once per year on thirty (30) days’ notice."
     *   quote     "once per year on thirty (30) days' notice"
     *   -> "once per year on thirty (30) days’ notice"
     */
    static quoteAsParagraphHoldsIt(paragraphText: string, quoteText: string): string | null {
        /** NULL when the quote is blank or no stretch of the paragraph reads as it: no first occurrence. */
        return EditOperations.occurrencesOf(paragraphText, quoteText)[0]?.text ?? null;
    }

    /**
     * Every stretch of `paragraphText` that reads as `quoteText` under the anchor fold
     * (`foldedForMatching`), in reading order and never overlapping, each as the
     * paragraph's own characters with its span in them: the one occurrence finder a
     * quote, a comment's words and a change made throughout a document are all found
     * with.
     *
     * WHY never overlapping
     *   Each occurrence is a stretch a change may replace; two that shared characters
     *   could not both be replaced, so the search resumes after the end of the last.
     *
     * RETURNS
     *   [] when the quote is blank or no stretch of the paragraph reads as it.
     *
     * EXAMPLE
     *   paragraph "Supplier’s fees. The Supplier pays. Supplier’s duty."   quote "Supplier's"
     *   -> [{ start: 0, end: 10, text: "Supplier’s" }, { start: 36, end: 46, text: "Supplier’s" }]
     *   ("Supplier pays" does not read as "Supplier's")
     */
    static occurrencesOf(paragraphText: string, quoteText: string): QuoteOccurrence[] {
        /**
         * The quote as the paragraph's accepted text spells what the prompt showed, as an
         * edit's anchor is read (`promptTextToAcceptedText`): "Fees &amp; costs" -> "Fees & costs",
         * "⟦ref: Section 7.19⟧" -> "Section 7.19", "due<br/>thirty" -> "due" + line break + "thirty".
         */
        const quoteAsAcceptedText = EditOperations.promptTextToAcceptedText(quoteText);
        const foldedQuote = WordTokenizer.foldForMatching(quoteAsAcceptedText).text.trim();
        /** WHEN the quote is only whitespace CONSEQUENCE it names no words: no occurrence. */
        if (foldedQuote.length === 0) return [];
        const foldedParagraph = WordTokenizer.foldForMatching(paragraphText);
        const occurrences: QuoteOccurrence[] = [];
        let foldedQuoteStart = foldedParagraph.text.indexOf(foldedQuote);
        while (foldedQuoteStart !== -1) {
            /**
             * The folded quote's last character, `start + length - 1`, whose source end closes
             * the stretch.
             *
             * EXAMPLE
             *   quote folded to "days' notice" (12) found at 72 -> last folded index 83; source 72..84
             */
            const foldedQuoteLast = foldedQuoteStart + foldedQuote.length - 1;
            const start = foldedParagraph.sourceStartByFoldedIndex[foldedQuoteStart];
            const end = foldedParagraph.sourceEndByFoldedIndex[foldedQuoteLast];
            occurrences.push({ start, end, text: paragraphText.slice(start, end) });
            /**
             * EXAMPLE
             *   a 12-character quote found at 72 -> the next search starts at 72 + 12 = 84, past its last character
             */
            foldedQuoteStart = foldedParagraph.text.indexOf(foldedQuote, foldedQuoteStart + foldedQuote.length);
        }
        return occurrences;
    }

    /**
     * The span a `replace_span` names by its quote and its place among the quote's
     * occurrences (`occurrence`, counted from 1 in reading order): how a change made
     * throughout a document picks one of several identical words in one paragraph,
     * which a quote alone hands back as ambiguous.
     *
     * WHEN the paragraph holds fewer occurrences than `occurrence` names
     *   CONSEQUENCE
     *     ask again, naming how many there are: the paragraph changed since the
     *     occurrences were counted.
     *
     * EXAMPLE
     *   text "Supplier pays. The Supplier delivers."   anchor "Supplier", occurrence 2
     *   occurrences at 0 and 19 -> index 2 - 1 = 1 -> { start: 19, end: 27 }
     */
    private static locateNthOccurrence(text: string, anchor: string, occurrence: number, operationIndex: number): AnchorSpan {
        const occurrences = EditOperations.occurrencesOf(text, anchor);
        /**
         * EXAMPLE
         *   occurrence 2 -> occurrences[1], the second in reading order
         */
        const namedOccurrence = occurrences[occurrence - 1];
        if (namedOccurrence === undefined) {
            throw new EditOperationReask(
                operationIndex,
                "occurrenceMissing",
                `edit_ops[${operationIndex}] (replace_span): anchor "${EditOperations.preview(anchor)}" occurs ${occurrences.length} time(s) in the paragraph, so occurrence ${occurrence} names none of them. ${EditOperations.anchorOnCurrentTextInstruction(text)}`,
            );
        }
        return { start: namedOccurrence.start, end: namedOccurrence.end };
    }

    /**
     * Undoes what the prompt did to the prose so an anchor copied from it matches
     * the accepted text: the reference marks removed (`⟦ref: Section 7.19⟧` back to
     * `Section 7.19`, so an anchor quoted across one lands on the reference text
     * the paragraph really holds), `<br/>` back to a line break, then the five XML
     * entities back to their characters. The ampersand goes last so `&amp;lt;` does
     * not become `<`.
     */
    private static promptTextToAcceptedText(promptText: string): string {
        return EditOperations.withoutReferenceMarks(promptText)
            .replace(/<br\s*\/?>/gi, "\n")
            .replace(/&lt;/g, "<")
            .replace(/&gt;/g, ">")
            .replace(/&quot;/g, '"')
            .replace(/&apos;/g, "'")
            .replace(/&amp;/g, "&");
    }

    /**
     * The prompt's reference marks taken off a piece of text: they are the
     * prompt's way of pointing at a cross-reference, page number, date or
     * equation, and no document ever holds them.
     *
     * EXAMPLE
     *   "See ⟦ref: Section 2.1⟧ for the cap." -> "See Section 2.1 for the cap."
     */
    private static withoutReferenceMarks(text: string): string {
        return text.split(REFERENCE_MARK_OPEN).join("").split(REFERENCE_MARK_CLOSE).join("");
    }

    /**
     * The words an operation writes into the document, exactly as given once read
     * back out of the prompt's escaping by the same decode its anchor goes through
     * (`promptTextToAcceptedText`): the model quotes the words from the prompt,
     * so the words it sends carry the prompt's reference marks, its `<br/>` and
     * its XML entities, and every one of them names a character the contract
     * must end up holding rather than characters to write literally.
     *
     * EXAMPLE
     *   text written by the model             -> characters written to the document
     *   "Fees for R&amp;D work."              -> "Fees for R&D work."
     *   "Interest accrues at &lt;10%."        -> "Interest accrues at <10%."
     *   "See ⟦ref: Section 2.1⟧ for the cap." -> "See Section 2.1 for the cap."
     *
     * Nothing is trimmed: a space the model leads its text with is the space between
     * the paragraph's last sentence and its own, and is written.
     *
     * WHEN an operation that adds words carries none, or only whitespace
     *   CONSEQUENCE
     *     ask again:the model meant a deletion, or forgot the text. The whitespace is only
     *     read to ask again; it is never removed from text that also carries words.
     */
    private static requireWordsToWrite(text: string, operationName: string, operationIndex: number): string {
        const wordsToWrite = EditOperations.promptTextToAcceptedText(text);
        if (wordsToWrite.trim().length > 0) return wordsToWrite;
        throw new EditOperationReask(operationIndex, "emptyText", `edit_ops[${operationIndex}] (${operationName}): text is empty — supply the words to insert, or use delete_span to remove a span.`);
    }

    /**
     * The words an operation writes inside a paragraph (`requireWordsToWrite`),
     * handed back when they start or end with a line break.
     *
     * WHEN they start or end with a line break
     *   CONSEQUENCE
     *     ask again:inside a paragraph a line break is a soft break within that paragraph.
     *     A model that leads or ends its text with one means a new paragraph, which only
     *     `insert_paragraph_after` makes; 11 of 83 prod engine and live-review
     *     `insert_after_anchor` texts measured on 2026-10-13 did, each meant as a
     *     clause of its own.
     */
    private static requireWordsWrittenInParagraph(text: string, operationName: string, operationIndex: number, seamConfirmations: SeamConfirmations | null): string {
        const wordsAsGiven = EditOperations.requireWordsToWrite(text, operationName, operationIndex);
        if (!LINE_BREAK_AT_TEXT_EDGE.test(wordsAsGiven)) return wordsAsGiven;
        EditOperations.askAgainUnlessConfirmed(
            operationIndex,
            "textLineBreakAtEdge",
            `edit_ops[${operationIndex}] (${operationName}): text starts or ends with a line break. Text is written exactly as given inside this paragraph, so the break would stay inside it and start no new paragraph — to add a paragraph use insert_paragraph_after; otherwise remove the line break.`,
            seamConfirmations,
        );
        return wordsAsGiven;
    }

    /**
     * The paragraph with `wordsAsGiven` appended exactly as given: one join, at the
     * paragraph's end, which stands in for the paragraph's last character and the end
     * that followed it.
     *
     * EXAMPLE
     *   paragraphText = "written notice."   (length 15)
     *   wordsAsGiven  = " The parties"
     *   text          -> "written notice. The parties", joinOffset 15, the " "
     */
    private static appendedAsWritten(paragraphText: string, wordsAsGiven: string): ParagraphTextAsWritten {
        return {
            text: paragraphText + wordsAsGiven,
            joins: [{ joinOffset: paragraphText.length, paragraphTextFollowsJoin: false, paragraphCharactersReplacedByJoin: [{ characterBeforeCut: EditOperations.lastCharacterOf(paragraphText), characterAfterCut: "" }] }],
        };
    }

    /**
     * The paragraph with one anchored operation written in exactly as given, and the
     * joins its text makes with the paragraph's.
     *
     * CASES, with `before` the text up to the anchor, `anchor` the anchor and `after` the rest
     *   - `insert_after_anchor`  -> before + anchor + words + after; both joins stand in for
     *                               the pair at the anchor's end
     *   - `insert_before_anchor` -> before + words + anchor + after; both joins stand in for
     *                               the pair at the anchor's start
     *   - `replace_span`         -> before + words + after; the first join stands in for the
     *                               pair at the anchor's start, the second for its end
     *   - `delete_span`          -> before + after; one join, standing in for both pairs
     *
     * Every join but the last of an insertion or replacement is followed by the
     * operation's own words; the last, and a deletion's one join, by the paragraph's.
     *
     * EXAMPLE, `insert_after_anchor`
     *   text         = "notice given by a party"   anchorSpan { start: 7, end: 17 }, "given by a"
     *   wordsAsGiven = " Customer or"   (length 12)
     *   text written -> "notice given by a Customer or party"
     *   first join   -> joinOffset 17, between "a" and " "
     *   `17 + 12`    -> joinOffset 29, between "r" and " party"
     *   both stand in for the paragraph's pair at the anchor's end, "a" and " "
     */
    private static splicedAsWritten(
        text: string,
        anchorSpan: AnchorSpan,
        operation: Extract<ParagraphEditOperation, { op: "insert_after_anchor" | "insert_before_anchor" | "replace_span" | "delete_span" }>,
        wordsAsGiven: string,
    ): ParagraphTextAsWritten {
        const beforeAnchor = text.slice(0, anchorSpan.start);
        const anchorText = text.slice(anchorSpan.start, anchorSpan.end);
        const afterAnchor = text.slice(anchorSpan.end);
        const charactersAtAnchorStart: ParagraphCharactersAtCut = { characterBeforeCut: EditOperations.lastCharacterOf(beforeAnchor), characterAfterCut: EditOperations.firstCharacterOf(anchorText) };
        const charactersAtAnchorEnd: ParagraphCharactersAtCut = { characterBeforeCut: EditOperations.lastCharacterOf(anchorText), characterAfterCut: EditOperations.firstCharacterOf(afterAnchor) };
        if (operation.op === "insert_after_anchor") {
            const anchorEndOffset = anchorSpan.end;
            return {
                text: beforeAnchor + anchorText + wordsAsGiven + afterAnchor,
                joins: [
                    { joinOffset: anchorEndOffset, paragraphTextFollowsJoin: false, paragraphCharactersReplacedByJoin: [charactersAtAnchorEnd] },
                    { joinOffset: anchorEndOffset + wordsAsGiven.length, paragraphTextFollowsJoin: true, paragraphCharactersReplacedByJoin: [charactersAtAnchorEnd] },
                ],
            };
        }
        if (operation.op === "insert_before_anchor") {
            return {
                text: beforeAnchor + wordsAsGiven + anchorText + afterAnchor,
                joins: [
                    { joinOffset: anchorSpan.start, paragraphTextFollowsJoin: false, paragraphCharactersReplacedByJoin: [charactersAtAnchorStart] },
                    { joinOffset: anchorSpan.start + wordsAsGiven.length, paragraphTextFollowsJoin: true, paragraphCharactersReplacedByJoin: [charactersAtAnchorStart] },
                ],
            };
        }
        if (operation.op === "replace_span") {
            return {
                text: beforeAnchor + wordsAsGiven + afterAnchor,
                joins: [
                    { joinOffset: anchorSpan.start, paragraphTextFollowsJoin: false, paragraphCharactersReplacedByJoin: [charactersAtAnchorStart] },
                    { joinOffset: anchorSpan.start + wordsAsGiven.length, paragraphTextFollowsJoin: true, paragraphCharactersReplacedByJoin: [charactersAtAnchorEnd] },
                ],
            };
        }
        return { text: beforeAnchor + afterAnchor, joins: [{ joinOffset: anchorSpan.start, paragraphTextFollowsJoin: true, paragraphCharactersReplacedByJoin: [charactersAtAnchorStart, charactersAtAnchorEnd] }] };
    }

    /**
     * Asks the model again for an operation whose text, written exactly as given, glues or doubles what
     * the paragraph keeps apart at one of its joins. The text is never changed to fit:
     * the model is told what would read there and resends.
     *
     * Each check compares the two characters meeting at the join with the pair the
     * paragraph held at that cut. A join of the same shape as the paragraph's own pair
     * passes, so an edit inside a word ("regulator" + "s") or beside the paragraph's
     * own tight punctuation ("clause 4.Definitions") is written as asked.
     *
     * CASES, checked in this order at each join
     *   - the paragraph's text after the join opens with a mark   -> "textDetachesAccent"
     *   - two gap characters meet (a space, a tab, a no-break space) -> "textDoublesSpace"
     *   - one punctuation mark meets itself                       -> "textDoublesPunctuation"
     *   - the text's own `.` meets the paragraph's `,`            -> "textDoublesPunctuation"
     *   - `.` `?` `!` `;` `:` meets a letter of a spaced script   -> "textGluesSentences"
     *   - two letters of spaced scripts meet                      -> "textGluesWords"
     *
     * EXAMPLE, "textDoublesPunctuation" (live test 3.6, 2026-10-15)
     *   paragraph  "…in the twelve (12) months preceding the event giving rise to the claim."
     *   operation  replace_span from "exceed the total fees…giving rise to" to "the claim",
     *              text "exceed the fees paid in the prior year."
     *   span       stops before the paragraph's own "."
     *   would read "…paid in the prior year.."   the text's "." meets the paragraph's
     *   pair at cut "m" + "."                      no such pair, so it is handed back
     *
     * EXAMPLE, a full stop before a comma (item 12, decision 4, R26)
     *   paragraph  "Supplier shall perform the Services."
     *   operation  insert_after_anchor "the Services.", text ", and shall provide reports"
     *   would read "…the Services., and shall provide reports"   the paragraph's "." meets the
     *                                                            text's ",": asked once which reading
     *                                                            the model meant, and its next answer
     *                                                            is written as given
     *
     * WHY a mark after the join is handed back outright
     *   A mark (an accent stored as its own character, a Thai or Hindi vowel sign)
     *   belongs to the character before it. When the paragraph's text after a join
     *   opens with one, the cut fell between a character and its mark, and whatever
     *   the operation writes before the join would take the mark: inserting
     *   " Holding" after "Nestle" in "Nestle" + U+0301 reads "Nestle Holding" + U+0301,
     *   the accent on the "g". No character of the paragraph is wrong to quote, so
     *   the model is asked to extend the anchor past the mark.
     *
     * WHY only scripts that space their words
     *   Japanese, Chinese and Thai text runs without spaces between its words, and
     *   writes Latin words against its own letters with none ("ユーザーID"). None of
     *   their letters ever trips a check, so text in them is written as given.
     */
    private static askAgainWhenJoinsGlueOrDouble(paragraphAsWritten: ParagraphTextAsWritten, operationName: string, operationIndex: number, seamConfirmations: SeamConfirmations | null): void {
        const isDeletion = operationName === "delete_span";
        for (const join of paragraphAsWritten.joins) {
            const characterBeforeJoin = EditOperations.lastCharacterOf(paragraphAsWritten.text.slice(0, join.joinOffset));
            const characterAfterJoin = EditOperations.firstCharacterOf(paragraphAsWritten.text.slice(join.joinOffset));
            const paragraphHadSameShapeAtCut = (shape: (characterBefore: string, characterAfter: string) => boolean): boolean =>
                join.paragraphCharactersReplacedByJoin.some((charactersAtCut) => shape(charactersAtCut.characterBeforeCut, charactersAtCut.characterAfterCut));
            const wouldRead = `edit_ops[${operationIndex}] (${operationName}): text is written exactly as given, and here the paragraph would read "${EditOperations.joinPreview(paragraphAsWritten.text, join.joinOffset)}"`;
            if (join.paragraphTextFollowsJoin && COMBINING_MARK.test(characterAfterJoin)) {
                EditOperations.askAgainUnlessConfirmed(
                    operationIndex,
                    "textDetachesAccent",
                    `${wouldRead}: the anchor ends between a character and the accent or mark stored after it, so the mark would move onto other text. Extend the anchor to take in the mark, then resubmit.`,
                    seamConfirmations,
                );
            }
            if (EditOperations.spacesMeet(characterBeforeJoin, characterAfterJoin) && !paragraphHadSameShapeAtCut(EditOperations.spacesMeet)) {
                const remedy = isDeletion ? "Include one of the two gap characters in the deleted span." : "Leave the gap out of the text; the paragraph already has one there.";
                EditOperations.askAgainUnlessConfirmed(operationIndex, "textDoublesSpace", `${wouldRead}, with a doubled gap: two whitespace characters (a space, a tab or a no-break space) in a row. ${remedy}`, seamConfirmations);
            }
            if (EditOperations.punctuationMeetsItself(characterBeforeJoin, characterAfterJoin) && !paragraphHadSameShapeAtCut(EditOperations.punctuationMeetsItself)) {
                /**
                 * CASES
                 *   - a deletion closed on two of the mark -> take one of them into the deleted span
                 *   - the text brought its own mark        -> leave it out; the paragraph's stays
                 */
                const remedy = isDeletion ? `Include one of the two "${characterAfterJoin}" in the deleted span.` : `Leave the "${characterAfterJoin}" out of the text; the paragraph already has one there.`;
                EditOperations.askAgainUnlessConfirmed(operationIndex, "textDoublesPunctuation", `${wouldRead}, with "${characterBeforeJoin}${characterAfterJoin}" in a row. ${remedy}`, seamConfirmations);
            }
            /**
             * WHEN a "." meets a "," at the join, read through the fold so "．" and "，" are the
             *      same marks, where the paragraph had no such pair
             *   CONSEQUENCE
             *     the model is asked which reading it meant, naming both: the "." ends a
             *     sentence, or it closes an abbreviation that keeps it before the ","; the
             *     text is never changed to fit (item 12, decision 4)
             *
             * CASES, by the core that posted the operations
             *   - it posts confirmations, this operation not confirmed -> asked once (`askedOnce`), whichever
             *                                                             side wrote the ".": the paragraph's
             *                                                             "Services." with ", and shall provide
             *                                                             reports" written after it (R26)
             *   - this operation confirmed after that ask              -> written as given
             *   - it posts none (a core from before confirmations)     -> the text's own "." meeting the
             *                                                             paragraph's "," asked as before; any
             *                                                             other "." before a "," written as given
             */
            const fullStopMeetsCommaHere = EditOperations.fullStopMeetsComma(characterBeforeJoin, characterAfterJoin) && !paragraphHadSameShapeAtCut(EditOperations.fullStopMeetsComma);
            const thisCoreAsksAboutIt = seamConfirmations === null ? !isDeletion && join.paragraphTextFollowsJoin : !seamConfirmations.confirmedOperationIndexes.has(operationIndex);
            if (fullStopMeetsCommaHere && thisCoreAsksAboutIt) {
                /**
                 * The word the "." closes: the last word of the text up to the join, `joinOffset`
                 * being the index of the first character after it.
                 *
                 * EXAMPLE
                 *   "to Acme Asset Mgmt., and"   joinOffset 19, the ","   -> text 0..19 "to Acme Asset Mgmt."
                 *   -> its last word: "Mgmt."
                 *
                 * FALLBACK
                 *   "." alone, never reached: the character before the join is the ".", so the text
                 *   up to the join always holds a word.
                 */
                const wordsBeforeJoin = WordTokenizer.words(paragraphAsWritten.text.slice(0, join.joinOffset));
                const lastWordBeforeJoin = wordsBeforeJoin[wordsBeforeJoin.length - 1] ?? ".";
                throw new EditOperationReask(
                    operationIndex,
                    seamConfirmations === null ? "textDoublesPunctuation" : "textFullStopBeforeComma",
                    `${wouldRead}, with "," straight after the "." of "${lastWordBeforeJoin}". ${EditOperations.fullStopBeforeCommaReadings(lastWordBeforeJoin, isDeletion, join.paragraphTextFollowsJoin, seamConfirmations !== null)}`,
                );
            }
            if (EditOperations.sentenceRunsIntoWord(characterBeforeJoin, characterAfterJoin) && !paragraphHadSameShapeAtCut(EditOperations.sentenceRunsIntoWord)) {
                const remedy = isDeletion ? "Leave the space after the sentence out of the deleted span." : "Add the space to the text.";
                EditOperations.askAgainUnlessConfirmed(operationIndex, "textGluesSentences", `${wouldRead}, with no space after the sentence. ${remedy}`, seamConfirmations);
            }
            if (EditOperations.wordsMeet(characterBeforeJoin, characterAfterJoin) && !paragraphHadSameShapeAtCut(EditOperations.wordsMeet)) {
                const remedy = isDeletion ? "Leave one of the spaces either side of the span out of the deleted span." : "If the text starts a new word, add the space to the text.";
                EditOperations.askAgainUnlessConfirmed(operationIndex, "textGluesWords", `${wouldRead}, with no space between the words. ${remedy}`, seamConfirmations);
            }
        }
    }

    /**
     * Hands the operation back for `reason` unless the model has confirmed it: the one way a
     * check whose reason is written as given (`EDIT_OPERATION_REASK_POLICIES`) raises it. The
     * type takes only those reasons, so no check lets through an operation that has nothing
     * as given to write.
     *
     * CASES
     *   - the core posted this operation confirmed (asked about it, the model confirmed
     *     it and the core sent it again)                  -> nothing: written as given
     *   - otherwise                                       -> the re-ask; `applyOperationList`
     *                                                        ends it with how to confirm when
     *                                                        the core confirms answers
     *
     * EXAMPLE
     *   "GES" inserted after "TRACKED CHAN", first ask -> handed back, '…If this is what you meant, confirm it…'
     *   the same operation sent again, now confirmed   -> written: "TRACKED CHANGES"
     */
    private static askAgainUnlessConfirmed(operationIndex: number, reason: EditOperationReaskWrittenAsGiven, message: string, seamConfirmations: SeamConfirmations | null): void {
        if (seamConfirmations !== null && seamConfirmations.confirmedOperationIndexes.has(operationIndex)) return;
        throw new EditOperationReask(operationIndex, reason, message);
    }

    /** Two whitespace characters side by side: two spaces, or a tab beside a space (`WordTokenizer.isWhitespace`, the one gap predicate). */
    private static spacesMeet(characterBefore: string, characterAfter: string): boolean {
        return WordTokenizer.isWhitespace(characterBefore) && WordTokenizer.isWhitespace(characterAfter);
    }

    /**
     * One punctuation mark that is written once (`WordTokenizer.isPunctuationWrittenOnce`),
     * side by side with itself as the fold reads it: "..", ",,", ";;", "。。", and a mark
     * beside its other-width form, ",，" or ".．". Two different marks ("?!", ".,") are not
     * this case.
     *
     * EXAMPLE
     *   "费用," + "，乙方"  -> "," meets "，", both folded ","  -> doubled
     *   "Inc．" + "."      -> "．" meets ".", both folded "."  -> doubled
     */
    private static punctuationMeetsItself(characterBefore: string, characterAfter: string): boolean {
        return WordTokenizer.isPunctuationWrittenOnce(characterBefore) && WordTokenizer.comparisonKey(characterBefore) === WordTokenizer.comparisonKey(characterAfter);
    }

    /** A full stop with a comma straight after it, read through the fold so "．，" is one too: ".,". */
    private static fullStopMeetsComma(characterBefore: string, characterAfter: string): boolean {
        return WordTokenizer.comparisonKey(characterBefore) === "." && WordTokenizer.comparisonKey(characterAfter) === ",";
    }

    /**
     * The two readings of a "." before a "," that a re-ask names, for the side that wrote the ".".
     *
     * CASES
     *   - a core that confirms, any side          -> the sentence reading's fix, or the same answer again,
     *                                                written as given
     *   - a core that does not                     -> the text's own "." (the only one it is asked about):
     *                                                leave it out, or extend the span over the ","
     *   - a deletion closed the two marks          -> include one of them in the deleted span
     *   - the paragraph's "." met the text's ","   -> start the text as a sentence of its own
     *   - the text's "." met the paragraph's ","   -> leave the "." out of the text
     */
    private static fullStopBeforeCommaReadings(wordTheFullStopCloses: string, isDeletion: boolean, paragraphTextFollowsJoin: boolean, coreConfirmsAnswers: boolean): string {
        const abbreviationReading = coreConfirmsAnswers
            ? `If "${wordTheFullStopCloses}" is an abbreviation that keeps its ".", confirm this operation; it is written as given.`
            : `If "${wordTheFullStopCloses}" is an abbreviation that keeps its ".", extend the span over the paragraph's "," so the text writes it after the ".".`;
        if (isDeletion) return `If the "." ends a sentence, include the "," in the deleted span. ${abbreviationReading}`;
        if (!paragraphTextFollowsJoin) return `If the paragraph's "." ends its sentence, write the text as a sentence of its own after it. ${abbreviationReading}`;
        return `If that "." ends a sentence, leave it out of the text. ${abbreviationReading}`;
    }

    /**
     * Asks the model once about a mark it writes in another form than the paragraph
     * writes it (A5): the fold reads the two as one mark, so the engine would write the
     * model's form as a change the user sees, and the model is shown both forms.
     *
     * WHEN a word the engine writes for this operation holds a mark the paragraph writes
     *      only in another form ("Supplier's" where the paragraph writes "’", "2029" after
     *      a "-" where the paragraph writes "–")
     *   CONSEQUENCE
     *     asked once (`askedOnce`); the model's next answer is written as given, in the
     *     model's form or the document's
     *
     * CASES
     *   - a core that posts no confirmations, or
     *     this operation confirmed after the ask     -> nothing (`asksOnceAbout`)
     *   - the paragraph writes the model's form too  -> nothing: the document mixes the forms
     *   - the mark sits in a word that reads the same
     *     under the fold                             -> nothing: the engine keeps the paragraph's
     *                                                   word (`TextEditPlanner`), so it is never written
     *
     * EXAMPLE
     *   paragraph  "Customer may audit Supplier’s records on thirty (30) days’ notice."
     *   operation  insert_after_anchor "records", text " and Supplier's accounts"
     *   written    " and Supplier's accounts": "'" in "Supplier's", the paragraph writing only "’" -> asked once
     */
    private static askOnceWhenTextRetypesDocumentMarks(paragraphTextBefore: string, paragraphTextAfter: string, operationName: string, operationIndex: number, seamConfirmations: SeamConfirmations | null): void {
        if (!EditOperations.asksOnceAbout(operationIndex, seamConfirmations)) return;
        const textsTheEngineWrites = TextEditPlanner.editsBetween(paragraphTextBefore, paragraphTextAfter).map((plannedEdit) => plannedEdit.replacementText);
        EditOperations.askOnceWhenWrittenTextsRetypeMarks(textsTheEngineWrites, paragraphTextBefore, operationName, operationIndex);
    }

    /**
     * Asks the model once about a mark a new paragraph writes in another form than the
     * paragraph it follows writes it (A5), the ask `askOnceWhenTextRetypesDocumentMarks`
     * makes about an edit inside a paragraph. No word of a new paragraph is the
     * document's already, so every word of it is written and read.
     *
     * CASES
     *   - a core that posts no confirmations, or
     *     this operation confirmed after the ask          -> nothing (`asksOnceAbout`)
     *   - the paragraph it follows writes no form of the
     *     mark (a heading "8. Term" and an apostrophe)     -> nothing: no form to match
     *
     * EXAMPLE
     *   paragraph it follows  "Customer may audit Supplier’s records on thirty (30) days’ notice."
     *   operation             insert_paragraph_after, text "Supplier's auditors keep the results confidential."
     *   written               "'" in "Supplier's", the paragraph it follows writing only "’" -> asked once
     */
    private static askOnceWhenNewParagraphRetypesDocumentMarks(paragraphTextItFollows: string, newParagraphText: string, operationName: string, operationIndex: number, seamConfirmations: SeamConfirmations | null): void {
        if (!EditOperations.asksOnceAbout(operationIndex, seamConfirmations)) return;
        EditOperations.askOnceWhenWrittenTextsRetypeMarks([newParagraphText], paragraphTextItFollows, operationName, operationIndex);
    }

    /**
     * Whether this apply asks the model once about this operation's marks.
     *
     * CASES
     *   - a core that posts no confirmations      -> false: it cannot confirm an answer, so it would
     *                                                ask forever
     *   - this operation confirmed after the ask  -> false: the model answered again, written as given
     *   - any other operation                     -> true
     */
    private static asksOnceAbout(operationIndex: number, seamConfirmations: SeamConfirmations | null): boolean {
        return seamConfirmations !== null && !seamConfirmations.confirmedOperationIndexes.has(operationIndex);
    }

    /** `recordLinkInstructions` for one span operation: a `replace_span` alone says what becomes of a link; the other span operations carry no instruction. */
    private static recordLinkInstructionsOfSpanOperation(
        working: WorkingParagraph,
        paragraphText: string,
        anchorSpan: AnchorSpan,
        operation: PendingSpanOperation["operation"],
        operationIndex: number,
        seamConfirmations: SeamConfirmations | null,
    ): void {
        /** WHEN the operation inserts, appends or deletes CONSEQUENCE it carries no link instruction: a deletion that empties a link strikes it. */
        if (operation.op !== "replace_span") return;
        const writtenText = EditOperations.requireWordsWrittenInParagraph(operation.text, operation.op, operationIndex, seamConfirmations);
        EditOperations.recordLinkInstructions(working, paragraphText, anchorSpan, operation, writtenText, operationIndex, seamConfirmations);
    }

    /**
     * Records what becomes of every link whose words a replacement of `replacedSpan` by
     * `writtenText` changes, as the replacement's `link` says, or asks the model again.
     *
     * The links are read in the text the replacement was resolved against
     * (`linksInTextOf`): the paragraph as shown, or the running text an earlier operation
     * of the paragraph rewrote, so a replacement is judged the same on either.
     *
     * CASES
     *   - it changes no link's words                        -> nothing
     *   - it changes a link's words and says nothing of it   -> asked again (`linkInstructionMissing`)
     *   - it says what becomes of the link                   -> one instruction per link it changes
     *                                                         (`linkInstructionOf`)
     *
     * EXAMPLE
     *   "see the Fee Schedule now", link "the Fee Schedule" (https://x.example/fee) at 4..20
     *   replace_span "the Fee Schedule" -> "Schedule 1", link "change", link_url "https://x.example/s1"
     *   -> { action: "change", linkText: "the Fee Schedule", linkAddress: "https://x.example/fee",
     *        writtenText: "Schedule 1", newAddress: "https://x.example/s1" }
     *
     * EXAMPLE, on the running text
     *   edit_ops[1] replace_span "see" -> "refer to"; running "refer to the Fee Schedule now", link at 9..25
     *   edit_ops[2] replace_span "refer to the Fee Schedule" -> "refer to Annex C", no `link`
     *   -> edit_ops[2] asked again (`linkInstructionMissing`), as it is on the paragraph as shown
     */
    private static recordLinkInstructions(
        working: WorkingParagraph,
        paragraphText: string,
        replacedSpan: AnchorSpan,
        operation: LinkInstructedOperation,
        writtenText: string,
        operationIndex: number,
        seamConfirmations: SeamConfirmations | null,
    ): void {
        const linksChanged = EditOperations.linksChangedBy(EditOperations.linksInTextOf(working, paragraphText), paragraphText, replacedSpan, writtenText);
        /** EMPTY: the replacement changes no link's words. */
        if (linksChanged.length === 0) return;
        const linkAction = EditOperations.linkActionOf(operation);
        if (linkAction === null) {
            throw new EditOperationReask(
                operationIndex,
                "linkInstructionMissing",
                `edit_ops[${operationIndex}] (${operation.op}): its text changes the words of ${linksChanged.map(EditOperations.linkNamed).join(" and ")}. Say what becomes of the link: "link": "keep" keeps the new words in the link at its address, "link": "change" with "link_url" makes them a link to a new address, "link": "needs_url" writes them as plain text and tells the user the link needs an address.`,
            );
        }
        for (const changedLink of linksChanged) {
            working.linkInstructions.push(EditOperations.linkInstructionOf(changedLink, linkAction, operation, writtenText, operationIndex, seamConfirmations));
        }
    }

    /**
     * The paragraph's links in the offsets of `paragraphText`, the text a replacement was
     * resolved against.
     *
     * WHY carried and not read again
     *   The reader read the links on the paragraph as shown. Operations applied one after
     *   another rewrite the running text; read on the shown offsets, a later replacement's
     *   span indexed other words, its `link` was tied to no link, and one with no `link`
     *   struck the link with no word to the user. Carried through the planner's pieces
     *   between the two texts, the one plan the engine writes by, each link sits on the
     *   words it covers in the running text.
     *
     * CASES
     *   - `paragraphText` is the paragraph as shown -> its links as the reader read them
     *   - an earlier operation rewrote it           -> each link inside a stretch both texts keep
     *                                                  (`TextEditPlanner.piecesBetween`), moved by
     *                                                  that stretch's offset in the running text
     *
     * EDGE CASE
     *   A link an earlier operation reached sits in no kept stretch and is not carried: that
     *   operation's own instruction already says what becomes of it. Neither is a link in a
     *   kept stretch the running text spells with other spacing, whose characters no longer
     *   line up one for one.
     *
     * EXAMPLE
     *   shown   "see the Fee Schedule now", link 4..20
     *   running "refer to the Fee Schedule now"
     *   pieces  deleted "see" 0..3 | at 0, inserted "refer to" at 3 | 0..8, kept " the Fee Schedule now" 3..24 | 8..29
     *   -> link 4 + (8 - 3) = 9 .. 20 + (8 - 3) = 25
     */
    private static linksInTextOf(working: WorkingParagraph, paragraphText: string): ParagraphLink[] {
        /** WHEN no operation has rewritten the paragraph CONSEQUENCE the reader's offsets index it. */
        if (paragraphText === working.textAsShown) return working.linksAsShown;
        const keptStretches = TextEditPlanner.piecesBetween(working.textAsShown, paragraphText)
            .filter((piece) => piece.kind === "kept")
            /**
             * A stretch both texts spell alike: its source and running spans are the same length.
             * EXAMPLE " the Fee Schedule now" 3..24 | 8..29 -> 21 and 21, kept
             */
            .filter((piece) => piece.sourceEndOffset - piece.sourceStartOffset === piece.requestEndOffset - piece.requestStartOffset);
        return working.linksAsShown.flatMap((link) => {
            const keptStretchOfLink = keptStretches.find((piece) => piece.sourceStartOffset <= link.startOffset && link.endOffset <= piece.sourceEndOffset);
            /** EDGE CASE: an earlier operation reached the link, or respelled its stretch; not carried. */
            if (keptStretchOfLink === undefined) return [];
            /**
             * How far the stretch moved: its first character's offset in the running text less its offset as shown.
             * EXAMPLE kept 3..24 | 8..29 -> 8 - 3 = 5
             */
            const shiftIntoRunningText = keptStretchOfLink.requestStartOffset - keptStretchOfLink.sourceStartOffset;
            return [{ ...link, startOffset: link.startOffset + shiftIntoRunningText, endOffset: link.endOffset + shiftIntoRunningText }];
        });
    }

    /**
     * The links whose words a replacement of `replacedSpan` by `writtenText` changes:
     * those a words edit of the span reaches, planned by the planner the engine writes
     * the card with (`TextEditPlanner.editsBetween`), so a replacement that writes a
     * link's words back as they were changes no link.
     *
     * CASES, per planned edit and link (`ParagraphLinks.editReachesLinkText`, the reading
     * the engine's striker writes the link by)
     *   - the edit takes characters of the link's text -> it changes the link
     *   - it inserts strictly inside the link's text  -> it changes the link: the words go into it
     *   - it inserts at the link's start or its end   -> it does not: the engine writes it outside
     *   - it changes spacing alone                    -> it does not: the link reads the same
     *
     * EXAMPLE, "see the Fee Schedule now", link at 4..20
     *   span 0..24 -> "see the Fee Schedule today"  plans 21..24 -> "today": no link changed
     *   span 4..20 -> "Schedule 1"                  plans 4..20: the link changed
     */
    private static linksChangedBy(links: ParagraphLink[], paragraphText: string, replacedSpan: AnchorSpan, writtenText: string): ParagraphLink[] {
        /** EMPTY: a paragraph with no link has none to change. */
        if (links.length === 0) return [];
        /**
         * The replaced words: `start` is the span's first character, `end` the first one after it.
         *
         * EXAMPLE
         *   "see the Fee Schedule now", span 4..20 -> "the Fee Schedule"
         */
        const replacedText = paragraphText.slice(replacedSpan.start, replacedSpan.end);
        const wordEditsInParagraph = TextEditPlanner.editsBetween(replacedText, writtenText)
            .filter((plannedEdit) => plannedEdit.kind === "words")
            /**
             * The planned offsets index the replaced text; the span's start moves them into the paragraph.
             * EXAMPLE span 4..20, planned 0..16 -> `4 + 0`..`4 + 16` = 4..20
             */
            .map((plannedEdit) => ({ startOffset: replacedSpan.start + plannedEdit.startOffset, endOffset: replacedSpan.start + plannedEdit.endOffset }));
        return links.filter((link) => wordEditsInParagraph.some((wordEdit) => ParagraphLinks.editReachesLinkText(wordEdit, link)));
    }

    /**
     * The `link` a paragraph operation carries.
     *
     * NULL
     *   An operation that carries no instruction (an insertion, an appended sentence, a
     *   deletion), or a replacement that left `link` out.
     */
    private static linkActionOf(operation: ParagraphEditOperation): LinkAction | null {
        /** NULL: an insertion, a deletion or an appended sentence replaces no words and so says nothing of a link. */
        if (operation.op !== "replace_span" && operation.op !== "replace_paragraph") return null;
        /** NULL `link`: the core sends null for a field it left empty. */
        if (operation.link === undefined || operation.link === null) return null;
        return operation.link;
    }

    /**
     * The instruction for one link a replacement changes, by the replacement's `link`,
     * once the checks of that action pass.
     *
     * CASES
     *   - "keep"      -> asked once when the new words share none of the link's
     *                    (`askOnceWhenKeptLinkTakesOtherWords`) or the link's words are its
     *                    address (`askOnceWhenLinkTextIsItsAddress`)
     *   - "change"    -> asked again with no `link_url`, or one that is no address
     *                    (`requireNewLinkAddress`)
     *   - "needs_url" -> no check: the words are written as plain text and the user is told
     */
    private static linkInstructionOf(
        changedLink: ParagraphLink,
        linkAction: LinkAction,
        operation: LinkInstructedOperation,
        writtenText: string,
        operationIndex: number,
        seamConfirmations: SeamConfirmations | null,
    ): LinkInstruction {
        const instructedLink = { linkText: changedLink.displayText, linkAddress: changedLink.address, writtenText };
        switch (linkAction) {
            case "keep":
                EditOperations.askOnceWhenKeptLinkTakesOtherWords(changedLink, operation, writtenText, operationIndex, seamConfirmations);
                EditOperations.askOnceWhenLinkTextIsItsAddress(changedLink, operation, operationIndex, seamConfirmations);
                return { action: "keep", ...instructedLink };
            case "change":
                return { action: "change", ...instructedLink, newAddress: EditOperations.requireNewLinkAddress(operation, operationIndex) };
            case "needs_url":
                return { action: "needs_url", ...instructedLink };
        }
    }

    /**
     * The address a `link` "change" points the new words at: its `link_url`, once it reads
     * as an address a link can hold.
     *
     * CASES
     *   - no `link_url`, or a blank one       -> asked again (`linkAddressMissing`)
     *   - one the URL parser cannot read      -> asked again
     *   - one it reads ("https://…", "mailto:…") -> it, trimmed
     */
    private static requireNewLinkAddress(operation: LinkInstructedOperation, operationIndex: number): string {
        const newAddress = (operation.link_url ?? "").trim();
        /** WHEN the new address reads as one CONSEQUENCE it is the link's new address; otherwise the model is asked again below. */
        if (EditOperations.readsAsAnAddress(newAddress)) return newAddress;
        throw new EditOperationReask(
            operationIndex,
            "linkAddressMissing",
            `edit_ops[${operationIndex}] (${operation.op}): "link": "change" needs "link_url", the full address the new words link to, such as https://example.com/terms; "${newAddress}" is not one. If the address is not known, use "link": "needs_url".`,
        );
    }

    /** Whether a text reads as an address a link can hold: one the URL parser reads, a scheme first. */
    private static readsAsAnAddress(candidateAddress: string): boolean {
        /** EMPTY: no address. */
        if (candidateAddress.length === 0) return false;
        try {
            new URL(candidateAddress);
            return true;
        } catch {
            return false;
        }
    }

    /**
     * Asks the model once about a link kept around new words that share none of its words
     * (`WordTokenizer.contentKey`): the link's address then sits under words that may no
     * longer say what it points to.
     *
     * EXAMPLE
     *   link "the Fee Schedule", written "Annex B", "link": "keep" -> asked once
     *   link "the Fee Schedule", written "the Fee Schedules"         -> nothing: "fee" is kept
     */
    private static askOnceWhenKeptLinkTakesOtherWords(changedLink: ParagraphLink, operation: LinkInstructedOperation, writtenText: string, operationIndex: number, seamConfirmations: SeamConfirmations | null): void {
        /** WHEN the model already confirmed this operation, or the call asks nothing once (`asksOnceAbout`) CONSEQUENCE nothing to ask. */
        if (!EditOperations.asksOnceAbout(operationIndex, seamConfirmations)) return;
        const writtenContentWords = new Set(WordTokenizer.words(writtenText).map(WordTokenizer.contentKey));
        const sharesAWord = WordTokenizer.words(changedLink.displayText).some((linkWord) => writtenContentWords.has(WordTokenizer.contentKey(linkWord)));
        /** WHEN the new words keep one of the link's words CONSEQUENCE the link's words were edited: nothing to ask. */
        if (sharesAWord) return;
        throw new EditOperationReask(
            operationIndex,
            "linkKeptOverOtherWords",
            `edit_ops[${operationIndex}] (${operation.op}): "link": "keep" keeps ${EditOperations.linkNamed(changedLink)} around words that share none of its words ("${EditOperations.preview(writtenText)}"). If the link should point somewhere new, use "link": "change" with "link_url"; if its new address is not known, "link": "needs_url". Confirm it to keep the link around these words.`,
        );
    }

    /**
     * Asks the model once about a kept link whose words are a web address
     * (`WordTokenizer.webAddressesIn`): new words over it keep the old address under words
     * that may read as another.
     *
     * EXAMPLE
     *   link "https://x.example/fee", written "https://x.example/fees", "link": "keep" -> asked once
     */
    private static askOnceWhenLinkTextIsItsAddress(changedLink: ParagraphLink, operation: LinkInstructedOperation, operationIndex: number, seamConfirmations: SeamConfirmations | null): void {
        /** WHEN the model already confirmed this operation, or the call asks nothing once (`asksOnceAbout`) CONSEQUENCE nothing to ask. */
        if (!EditOperations.asksOnceAbout(operationIndex, seamConfirmations)) return;
        /** WHEN the link's words are not an address CONSEQUENCE nothing to ask. */
        if (WordTokenizer.webAddressesIn(changedLink.displayText).length === 0) return;
        throw new EditOperationReask(
            operationIndex,
            "linkTextIsItsAddress",
            `edit_ops[${operationIndex}] (${operation.op}): the words of ${EditOperations.linkNamed(changedLink)} are its address, and "link": "keep" keeps that address under new words. If the address changes with the words, use "link": "change" with the new "link_url". Confirm it to keep the address.`,
        );
    }

    /**
     * Asks the model once about a web address an operation writes as plain words
     * (`WordTokenizer.webAddressesIn`): it reads as an address and opens nothing.
     *
     * CASES
     *   - the operation makes its words a link (`link` "change")  -> nothing: the address is the link's
     *   - every address it writes the paragraph already reads      -> nothing: the operation keeps it
     *   - it writes an address the paragraph does not read         -> asked once
     *
     * EXAMPLE
     *   paragraph "Notices go by post.", insert_after_anchor "post" " or to https://x.example/notices" -> asked once
     */
    private static askOnceWhenTextWritesAnAddressAsWords(paragraphText: string, writtenText: string, operation: ParagraphEditOperation, operationIndex: number, seamConfirmations: SeamConfirmations | null): void {
        /** WHEN the model already confirmed this operation, or the call asks nothing once (`asksOnceAbout`) CONSEQUENCE nothing to ask. */
        if (!EditOperations.asksOnceAbout(operationIndex, seamConfirmations)) return;
        /** WHEN the operation makes its words a link CONSEQUENCE an address in them is the link's own. */
        if (EditOperations.linkActionOf(operation) === "change") return;
        const addressesTheParagraphReads = new Set(WordTokenizer.webAddressesIn(paragraphText));
        const newAddress = WordTokenizer.webAddressesIn(writtenText).find((writtenAddress) => !addressesTheParagraphReads.has(writtenAddress));
        /** WHEN the operation writes no address the paragraph does not already read CONSEQUENCE nothing to ask. */
        if (newAddress === undefined) return;
        throw new EditOperationReask(
            operationIndex,
            "textTypesAnAddress",
            `edit_ops[${operationIndex}] (${operation.op}): its text writes the web address "${newAddress}" as plain words, which will not open as a link. To link words, write them over a link of the paragraph with "link": "change" and "link_url". Confirm it to write the address as plain text.`,
        );
    }

    /**
     * A link as a re-ask names it: its words, and its address where the reader knew it.
     *
     * EXAMPLE
     *   "the Fee Schedule", https://x.example/fee -> 'the link "the Fee Schedule" (https://x.example/fee)'
     *   "Annex B", no address                    -> 'the link "Annex B"'
     */
    private static linkNamed(link: ParagraphLink): string {
        /** NULL address: the link is named by its words alone. */
        if (link.address === null) return `the link "${link.displayText}"`;
        return `the link "${link.displayText}" (${link.address})`;
    }

    /**
     * The one reading behind both asks about marks: the first word the engine writes that
     * holds a mark `paragraphTextWithDocumentMarks` writes only in another form is handed
     * back, asked once, naming both forms.
     *
     * CASES, per character of a written word
     *   - the paragraph writes no form of the mark, or writes this very form -> nothing to ask
     *   - the paragraph writes it only in other forms                        -> asked once
     */
    private static askOnceWhenWrittenTextsRetypeMarks(textsTheEngineWrites: readonly string[], paragraphTextWithDocumentMarks: string, operationName: string, operationIndex: number): void {
        const markFormsByFoldedMark = EditOperations.markFormsOf(paragraphTextWithDocumentMarks);
        for (const writtenText of textsTheEngineWrites) {
            for (const writtenWord of WordTokenizer.words(writtenText)) {
                for (const writtenCharacter of writtenWord) {
                    const paragraphForms = markFormsByFoldedMark.get(WordTokenizer.comparisonKey(writtenCharacter));
                    /** WHEN the paragraph writes no form of this mark, or writes this very form CONSEQUENCE nothing to ask about it. */
                    if (paragraphForms === undefined || paragraphForms.has(writtenCharacter)) continue;
                    const [paragraphForm] = paragraphForms;
                    throw new EditOperationReask(
                        operationIndex,
                        "textRetypesDocumentMark",
                        `edit_ops[${operationIndex}] (${operationName}): the text writes "${writtenCharacter}" in "${writtenWord}", where the paragraph writes "${paragraphForm}". Answer again with "${paragraphForm}" to match the document, or confirm this operation to keep "${writtenCharacter}"; it is then written as given.`,
                    );
                }
            }
        }
    }

    /**
     * The forms the paragraph writes each foldable mark in, keyed by the mark the fold reads
     * (`WordTokenizer.comparisonKey`). Letters, digits and whitespace are left out: they are
     * words and gaps, not marks.
     *
     * EXAMPLE
     *   "Supplier’s 2026–2028 terms." -> "'" -> {"’"}, "-" -> {"–"}, "." -> {"."}
     */
    private static markFormsOf(paragraphText: string): Map<string, Set<string>> {
        const markFormsByFoldedMark = new Map<string, Set<string>>();
        for (const paragraphCharacter of paragraphText) {
            if (WordTokenizer.holdsALetterOrDigit(paragraphCharacter) || WordTokenizer.isWhitespace(paragraphCharacter)) continue;
            const foldedMark = WordTokenizer.comparisonKey(paragraphCharacter);
            const markForms = markFormsByFoldedMark.get(foldedMark) ?? new Set<string>();
            markForms.add(paragraphCharacter);
            markFormsByFoldedMark.set(foldedMark, markForms);
        }
        return markFormsByFoldedMark;
    }

    /**
     * A mark that closes a sentence or a clause, in a form a space follows
     * (`WordTokenizer.closesSentenceOrClauseBeforeASpace`), with a letter of a spaced
     * script straight after it (`WordTokenizer.isLetterOfSpacedScript`).
     *
     * EXAMPLE
     *   "notice.The"    "समाप्त।अगला"   -> true: the next sentence glued on
     *   "発効する。Customer"   "notice．The"   -> false: the mark carries its own space
     */
    private static sentenceRunsIntoWord(characterBefore: string, characterAfter: string): boolean {
        return WordTokenizer.closesSentenceOrClauseBeforeASpace(characterBefore) && WordTokenizer.isLetterOfSpacedScript(characterAfter);
    }

    /** Two letters of spaced scripts side by side (`WordTokenizer.isLetterOfSpacedScript`). */
    private static wordsMeet(characterBefore: string, characterAfter: string): boolean {
        return WordTokenizer.isLetterOfSpacedScript(characterBefore) && WordTokenizer.isLetterOfSpacedScript(characterAfter);
    }

    /**
     * What a re-ask message quotes around a join: up to
     * `JOIN_PREVIEW_CHARACTERS_PER_SIDE` characters either side, counted as whole
     * characters, with every space kept so a doubled one shows.
     *
     * EXAMPLE
     *   text       = "Either party may terminate on sixty (60) days written notice.The parties may agree a shorter period."
     *   joinOffset = 61, the "T"
     *   left side  -> the 20 characters before 61:  "days written notice."
     *   right side -> the 20 characters from 61:    "The parties may agre"
     */
    private static joinPreview(text: string, joinOffset: number): string {
        const charactersBeforeJoin = Array.from(text.slice(0, joinOffset));
        const charactersAfterJoin = Array.from(text.slice(joinOffset));
        /**
         * EXAMPLE
         *   charactersBeforeJoin.length = 61
         *   `61 - 20`                   -> 41, so characters 41..60 are quoted
         *   charactersBeforeJoin.length = 12 -> `Math.max(0, 12 - 20)` = 0, all 12 quoted
         */
        const quotedBeforeJoin = charactersBeforeJoin.slice(Math.max(0, charactersBeforeJoin.length - JOIN_PREVIEW_CHARACTERS_PER_SIDE)).join("");
        const quotedAfterJoin = charactersAfterJoin.slice(0, JOIN_PREVIEW_CHARACTERS_PER_SIDE).join("");
        return quotedBeforeJoin + quotedAfterJoin;
    }

    /**
     * The last character of `text` as a reader counts it, a whole code point, so a
     * character outside the Basic Multilingual Plane is never read as half of its
     * surrogate pair; "" for an empty text.
     *
     * EXAMPLE
     *   text = "家𠮷"   UTF-16 length 3, the last two units one character
     *   `text.slice(-2)` -> both units of "𠮷" -> spread -> ["𠮷"] -> "𠮷"
     *   text = "ab"     `text.slice(-2)` -> "ab" -> ["a", "b"] -> "b"
     */
    private static lastCharacterOf(text: string): string {
        const lastTwoUnitsAsCharacters = Array.from(text.slice(-2));
        return lastTwoUnitsAsCharacters[lastTwoUnitsAsCharacters.length - 1] ?? "";
    }

    /**
     * The first character of `text`, a whole code point; "" for an empty text.
     *
     * EXAMPLE
     *   text = "𠮷家"   `text.slice(0, 2)` -> both units of "𠮷" -> ["𠮷"] -> "𠮷"
     */
    private static firstCharacterOf(text: string): string {
        return Array.from(text.slice(0, 2))[0] ?? "";
    }

    /**
     * The anchor as a re-ask message quotes it: whitespace collapsed, cut at
     * `ANCHOR_PREVIEW_LIMIT` characters with an ellipsis.
     */
    private static preview(anchor: string): string {
        const collapsed = anchor.split(/\s+/).join(" ");
        /**
         * EXAMPLE
         *   collapsed.length = 100, ANCHOR_PREVIEW_LIMIT = 80
         *   slice(0, 80)     -> characters 0..79, then "…"
         *   collapsed.length = 80 -> shown whole, no ellipsis
         */
        if (collapsed.length <= ANCHOR_PREVIEW_LIMIT) return collapsed;
        return `${collapsed.slice(0, ANCHOR_PREVIEW_LIMIT)}…`;
    }
}
